import { useSeoMeta } from '@unhead/react';
import React, { useState, useEffect, useCallback, useMemo, useRef, createContext, useContext } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  pointerWithin,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  type DragEndEvent,
  type DragStartEvent,
  type DragOverEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { LoginArea } from "@/components/auth/LoginArea";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostr } from '@nostrify/react';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLoginActions } from '@/hooks/useLoginActions';
import { RelayListManager } from '@/components/RelayListManager';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandShortcut } from "@/components/ui/command";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent as AlertDialogContentPrimitive,
  AlertDialogDescription as AlertDialogDescriptionPrimitive,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog.tsx';
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { MarkdownRenderer } from '@/components/MarkdownRenderer';
import { clearAllMessages } from '@/lib/dmMessageStore';
import {
  Panel,
  PanelGroup,
  PanelResizeHandle,
} from "react-resizable-panels";
import { Settings, X, Edit3, Eye, Columns3, RefreshCw, FileText, Database, Layout, File, BookOpen, Lock, Wifi, Sun, Moon, PanelLeft, Folder, ChevronRight, ChevronDown, ChevronLeft, FolderPlus, FilePlus, Trash2, GripVertical, FolderOpen, AlertTriangle, RotateCcw, Search } from "lucide-react";
import { useIsMobile } from '@/hooks/useIsMobile';
import { useToast } from '@/hooks/useToast';
import { useTheme } from '@/hooks/useTheme';
import { cn } from '@/lib/utils';
import { generateUUID, calculateChecksum } from '@/lib/nostrionUtils';

// Context to pass drag-handle pointer listeners down without attaching to root div
interface SortableNodeContextValue {
  handleProps: Record<string, unknown>;
}
const SortableNodeContext = createContext<SortableNodeContextValue>({ handleProps: {} });

// Sortable wrapper for FILE nodes — provides drag handle via context
function SortableFileNode({ id, children }: { id: string; children: React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
    position: 'relative' as const,
    zIndex: isDragging ? 50 : undefined,
  };
  const handleProps = { ...attributes, ...listeners };
  return (
    <SortableNodeContext.Provider value={{ handleProps }}>
      <div ref={setNodeRef} style={style}>
        {children}
      </div>
    </SortableNodeContext.Provider>
  );
}

// Droppable wrapper for FOLDER nodes — large, reliable drop zone with visual feedback
function DroppableFolderNode({ id, isDragActive, children }: { id: string; isDragActive: boolean; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      data-folder-drop={id}
      className={`rounded-xl transition-all duration-150 ${
        isDragActive && isOver
          ? 'ring-2 ring-primary/60 ring-offset-1 bg-primary/8'
          : ''
      }`}
    >
      {children}
    </div>
  );
}

// Dedicated droppable strip for the workspace root — only visible while dragging
function RootDropZone({ isDragActive }: { isDragActive: boolean }) {
  const { setNodeRef, isOver } = useDroppable({ id: 'folder:/' });
  if (!isDragActive) return null;
  return (
    <div
      ref={setNodeRef}
      className={`mx-1 mt-1 mb-0.5 flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-dashed text-xs transition-all duration-150 select-none ${
        isOver
          ? 'border-primary bg-primary/10 text-primary'
          : 'border-border text-muted-foreground/50'
      }`}
    >
      <Folder className="w-3 h-3 flex-shrink-0" />
      <span>Drop here → Workspace root</span>
    </div>
  );
}

// Drag handle element — spread pointer listeners from SortableNodeContext
function DragHandle() {
  const { handleProps } = useContext(SortableNodeContext);
  return (
    <span
      {...handleProps}
      onClick={(e) => e.stopPropagation()}
      className="flex-shrink-0 opacity-0 group-hover:opacity-50 cursor-grab touch-none inline-flex items-center"
    >
      <GripVertical className="w-3 h-3" />
    </span>
  );
}

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Module-level title derivation — handles both Nostrion payloads (which have
// an explicit `title` field) and Onyx payloads (which only have `path`).
// Onyx paths are relative without a leading slash: "Onyx Notes/file.md"
// Nostrion paths are absolute:                     "/notes/file.md"
// Both are handled identically — extract the last segment and prettify it.
// ---------------------------------------------------------------------------
function deriveTitleFromPayload(payload: Record<string, unknown> | undefined | null, fallbackDTag?: string): string {
  if (!payload) return fallbackDTag?.slice(0, 16) || 'Untitled';

  // Explicit title (Nostrion-created notes)
  const title = payload.title;
  if (title && typeof title === 'string' && title.trim()) return title.trim();

  // Derive from path (both Nostrion absolute and Onyx relative paths)
  const path = payload.path;
  if (path && typeof path === 'string') {
    // Strip leading slashes, split, take last segment, remove extension
    const basename = path.replace(/^\/+/, '').split('/').pop() || '';
    const name = basename.replace(/\.(md|markdown|txt)$/i, '').replace(/[-_]/g, ' ').trim();
    if (name) return name.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  }

  // Derive from first line of content
  const content = payload.content;
  if (content && typeof content === 'string') {
    const firstLine = content.split('\n')[0].replace(/^#+\s*/, '').replace(/^\s*[-*]\s*/, '').trim();
    if (firstLine && firstLine.length < 60) return firstLine.length > 40 ? firstLine.slice(0, 37) + '...' : firstLine;
  }

  return fallbackDTag?.slice(0, 16) || 'Untitled';
}

// Module-level helper: decrypt NIP-44 content with Onyx chunked-format support.
// Onyx splits large payloads (>60 KB) into multiple NIP-44 ciphertexts joined
// by '.' because NIP-44 has a hard 65,535-byte plaintext limit. The first chunk
// decrypts to {"_chunked":true,"total":N}; the remaining N chunks are the payload.
// Standard (unchunked) content is a single NIP-44 ciphertext with no '.'.
// ---------------------------------------------------------------------------
async function decryptOnyxContent(
  nip44signer: { decrypt: (pubkey: string, ciphertext: string) => Promise<string> },
  pubkey: string,
  ciphertext: string
): Promise<string> {
  // Fast path: no '.' means definitely not chunked
  if (!ciphertext.includes('.')) {
    return nip44signer.decrypt(pubkey, ciphertext);
  }

  const parts = ciphertext.split('.');

  // Try to decrypt the first segment as a chunked-format header.
  // If it throws (wrong key, bad format, unknown version) fall through to
  // treating the whole string as a plain NIP-44 ciphertext.
  let header: string;
  try {
    header = await nip44signer.decrypt(pubkey, parts[0]);
  } catch {
    // First segment not valid NIP-44 — the '.' must be incidental.
    // Try decrypting the whole ciphertext as-is.
    return nip44signer.decrypt(pubkey, ciphertext);
  }

  // If the header isn't a chunked marker, treat as plain ciphertext
  if (!header.startsWith('{"_chunked":true')) {
    return nip44signer.decrypt(pubkey, ciphertext);
  }

  const meta = JSON.parse(header) as { _chunked: boolean; total: number };
  if (parts.length !== meta.total + 1) {
    throw new Error(`Chunked payload: expected ${meta.total} data parts, got ${parts.length - 1}`);
  }

  const chunks: string[] = [];
  for (let i = 1; i < parts.length; i++) {
    chunks.push(await nip44signer.decrypt(pubkey, parts[i]));
  }
  return chunks.join('');
}

const Index = () => {
  useSeoMeta({
    title: 'Nostrion • Private Documents on Nostr',
    description: 'A Notion-inspired documents & databases app powered by Nostr. Private, encrypted, synced across devices.',
  });

  const isMobile = useIsMobile();
  const [currentView, setCurrentView] = useState<'notes' | 'database' | 'board'>('notes');
  const [noteContent, setNoteContent] = useState('');
  const [selectedNoteD, setSelectedNoteD] = useState<string | null>(null);
  const [notesMode, setNotesMode] = useState<'split' | 'edit' | 'preview'>('split');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // On mobile the sidebar is hidden by default and slides in as an overlay
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  // On mobile, search expands as a full-width bar when clicked
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [expandedPaths, setExpandedPaths] = useState<string[]>([]);

  // Stable identifier for the main note (prevents creating tons of duplicate events)
  const MAIN_NOTE_D = 'main-note';
  const KIND_FILE = 30800;
  const KIND_VAULT_INDEX = 30801;

  // Active vault d-tag — starts as 'default-vault' for backward-compat but is
  // overwritten with whatever d-tag is found in the relay's vault index (which may
  // be a UUID if created by Onyx). All vault index writes use this ref so Nostrion
  // always publishes to the same vault the user already has, even if Onyx created it.
  const activeVaultDRef = useRef<string>('default-vault');

  // Slash command menu state
  const [showSlashMenu, setShowSlashMenu] = useState(false);

  // New document modal
  const [showNewDocModal, setShowNewDocModal] = useState(false);
  const [newDocTitleInput, setNewDocTitleInput] = useState('');

  // Rename document modal
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renameDocD, setRenameDocD] = useState<string | null>(null);
  const [renameTitleInput, setRenameTitleInput] = useState('');

  // New folder modal
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [newFolderNameInput, setNewFolderNameInput] = useState('');
  const [newFolderParentPath, setNewFolderParentPath] = useState<string>('/');

  // Rename folder modal
  const [showRenameFolderModal, setShowRenameFolderModal] = useState(false);
  const [renameFolderPath, setRenameFolderPath] = useState<string | null>(null);
  const [renameFolderInput, setRenameFolderInput] = useState('');

  // Delete confirmation modal
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ dTag?: string; folderPath?: string; name: string; isFolder: boolean } | null>(null);

  // Drag and drop state
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  // Deleted file tombstones — mirrors Onyx's vault.data.deleted[] array
  // Each entry: { d, path, title, deletedAt, lastEventId? }
  const [deletedFiles, setDeletedFiles] = useState<{ d: string; path: string; title: string; deletedAt: number; lastEventId?: string }[]>([]);
  // Track the created_at of the most recent vault index we've processed locally.
  // Used to prevent a stale relay re-fetch from overwriting a more up-to-date optimistic state.
  const latestVaultTimestampRef = useRef<number>(0);
  // Scan-for-recoverable state (mirrors Onyx's "Scan for recoverable files")
  const [scanLoading, setScanLoading] = useState(false);
  const [scanMessage, setScanMessage] = useState<string | null>(null);
  const [scannedRecoverableFiles, setScannedRecoverableFiles] = useState<{ dTag: string; title: string; path: string; modifiedAt: number }[]>([]);
  const [recoveringScannedD, setRecoveringScannedD] = useState<string | null>(null);

  // Settings modal state (Obsidian-style)
  const [showSettings, setShowSettings] = useState(false);
  const [activeSettingsTab, setActiveSettingsTab] = useState<'relays' | 'general' | 'editor' | 'privacy' | 'appearance' | 'sync'>('general');
  // On mobile, whether to show the nav list or the drilled-in content panel
  const [mobileSettingsShowContent, setMobileSettingsShowContent] = useState(false);
  const [showWipeConfirm, setShowWipeConfirm] = useState(false);


  // Sync settings
  const [autoSyncEnabled, setAutoSyncEnabled] = useLocalStorage<boolean>('nostrion-auto-sync', true);
  // Start in 'syncing' state — the vault fetch + decrypt loop will flip it to 'synced' when done.
  const [syncStatus, setSyncStatus] = useState<'synced' | 'pending' | 'syncing'>('syncing');
  // Tracks whether the currently open note's content looks like a ciphertext blob
  // (double-encryption artifact from older versions). Used to show a warning.
  const [noteIsCorrupted, setNoteIsCorrupted] = useState(false);

  // Privacy settings - for complete privacy controls
  const [clearCacheOnLogout, setClearCacheOnLogout] = useLocalStorage<boolean>('nostrion-clear-cache-on-logout', true);
  const [autoClearInactivity, setAutoClearInactivity] = useLocalStorage<boolean>('nostrion-auto-clear-inactivity', false);

  // Database state - editable rows that sync to Nostr
  const [databaseRows, setDatabaseRows] = useState([
    { 
      id: '1', 
      task: "Implement rich editor with slash commands", 
      status: "In Progress", 
      assignee: "You", 
      due: "Today",
      d: 'db-task-1' 
    },
    { 
      id: '2', 
      task: "Add Nostr sync for database rows", 
      status: "Todo", 
      assignee: "You", 
      due: "Tomorrow",
      d: 'db-task-2' 
    },
    { 
      id: '3', 
      task: "Build fully editable database view", 
      status: "Done", 
      assignee: "You", 
      due: "Yesterday",
      d: 'db-task-3' 
    },
  ]);

  const { user } = useCurrentUser();
  const { nostr } = useNostr();
  const { mutateAsync: publishEvent } = useNostrPublish();
  const { toast } = useToast();
  const { logout } = useLoginActions();
  const { theme, setTheme } = useTheme();
  const queryClient = useQueryClient();

  // On mobile, split mode is too narrow — auto-switch to edit mode
  useEffect(() => {
    if (isMobile && notesMode === 'split') {
      setNotesMode('edit');
    }
  }, [isMobile, notesMode]);

  const sidebarItems = [
    { id: 'notes' as const, label: 'Documents', icon: FileText },
    { id: 'database' as const, label: 'Database', icon: Database },
    { id: 'board' as const, label: 'Project Board', icon: Layout },
  ];

  const activeItem = sidebarItems.find(item => item.id === currentView);

  // Full Nostr sync with decryption
  // staleTime: 60s prevents TanStack from background-refetching while optimistic moves are in-flight
  // refetchOnMount: 'always' ensures the vault loads every time the component mounts (login / page refresh)
  const { data: rawNotes, refetch: refetchNotes } = useQuery({
    queryKey: ['nostrion-notes', user?.pubkey],
    queryFn: async () => {
      if (!user?.pubkey || !nostr) return [];
      const events = await nostr.query([{
        kinds: [KIND_FILE, KIND_VAULT_INDEX],
        authors: [user.pubkey],  // CRITICAL for privacy - only fetch own events
        limit: 200,
      }]);
      return events;
    },
    enabled: !!user?.pubkey && !!nostr,
    staleTime: 60_000, // 60 seconds — prevents race between optimistic updates and background refetch
    refetchOnMount: 'always', // always re-fetch on mount so vault loads on login / page refresh
  });

  const [decryptedNotes, setDecryptedNotes] = useState<any[]>([]);
  const decryptedNotesRef = useRef(decryptedNotes);
  const [selectedNote, setSelectedNote] = useState<any>(null);

  // Keep ref updated with latest decryptedNotes for use in callbacks
  useEffect(() => {
    decryptedNotesRef.current = decryptedNotes;
  }, [decryptedNotes]);

  // Auto-refetch vault whenever the logged-in user changes (login event / page refresh with saved session).
  // This ensures the vault loads immediately after the signer is ready, without requiring a manual refresh.
  const prevPubkeyRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const pubkey = user?.pubkey;
    if (pubkey && pubkey !== prevPubkeyRef.current) {
      prevPubkeyRef.current = pubkey;
      // Show syncing immediately so the user sees the vault is loading
      setSyncStatus('syncing');
      // Small delay so the signer and relay connections are fully initialized
      const timer = setTimeout(() => {
        refetchNotes();
      }, 500);
      return () => clearTimeout(timer);
    }
    if (!pubkey) {
      prevPubkeyRef.current = undefined;
    }
  }, [user?.pubkey, refetchNotes]);

  // Thin wrapper around the module-level deriveTitleFromPayload so existing
  // call-sites that pass a full note object continue to work.
  const getNoteTitle = (note: any): string => {
    const dTag = note.tags?.find((t: string[]) => t[0] === 'd')?.[1];
    return deriveTitleFromPayload(note.payload, dTag);
  };

  // Detect if a string looks like a NIP-44 ciphertext blob (double-encryption artifact).
  // NIP-44 ciphertexts are base64-encoded and start with a version byte — "A" in base64.
  // Real markdown content never starts with a long base64 string without whitespace.
  const looksLikeCiphertext = useCallback((content: string): boolean => {
    if (!content || content.length < 64) return false;
    const trimmed = content.trim();
    // NIP-44 v2 ciphertexts start with "A" and are pure base64 with no spaces or newlines for 60+ chars
    return /^[A-Za-z0-9+/]{60,}/.test(trimmed) && !trimmed.includes(' ') && !trimmed.startsWith('#');
  }, []);

  // Toggle a specific task in markdown content by index (for interactive checkboxes in preview/split view)
  // Matches Onyx behavior: clicking checkbox toggles [ ]/[x] and applies strikethrough for completed tasks
  const toggleTaskAtIndex = (content: string, taskIndex: number): string => {
    const lines = content.split('\n');
    let taskCount = 0;
    for (let i = 0; i < lines.length; i++) {
      // Match: optional whitespace, bullet (-/*/+), [x or space], then text
      const match = lines[i].match(/^(\s*)([-*+])\s+\[([ xX])\]\s*(.*)$/);
      if (match) {
        if (taskCount === taskIndex) {
          const [, indent, bullet, checked, text] = match;
          const isChecked = checked.toLowerCase() === 'x';
          const newChecked = isChecked ? ' ' : 'x';
          
          let newText = text.trim();
          if (!isChecked) {
            // Mark as completed with strikethrough
            if (!newText.startsWith('~~') || !newText.endsWith('~~')) {
              newText = `~~${newText}~~`;
            }
          } else {
            // Remove strikethrough for incomplete
            newText = newText.replace(/^~~(.*?)~~$/, '$1').trim();
          }
          
          lines[i] = `${indent}${bullet} [${newChecked}] ${newText}`;
          return lines.join('\n');
        }
        taskCount++;
      }
    }
    return content; // no change if index not found
  };

  // Handler for interactive task-list checkboxes in MarkdownRenderer.
  // Always writes back: updates local state immediately for instant visual feedback,
  // then persists via the same path used for normal editor changes.
  // When auto-sync is on, setting syncStatus='pending' triggers the debounced save.
  // When auto-sync is off, we call saveNoteToNostr directly so the toggle still persists.
  const handleToggleTask = useCallback((taskIndex: number) => {
    const newContent = toggleTaskAtIndex(noteContent, taskIndex);
    if (newContent === noteContent) return; // nothing changed — bail early
    setNoteContent(newContent);
    if (autoSyncEnabled) {
      if (syncStatus !== 'syncing') setSyncStatus('pending');
    } else {
      // Auto-sync is off: persist immediately so the checkbox change isn't lost
      saveNoteToNostr(newContent, selectedNoteD || undefined);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteContent, autoSyncEnabled, syncStatus, selectedNoteD]);

  // Processed list of documents for sidebar - prefers vault index (30801) as source of truth
  // Excludes tombstoned (deleted) files — mirrors Onyx's vault.data.deleted[]
  const documentList = useMemo(() => {
    const deletedDSet = new Set(deletedFiles.map(f => f.d));
    const index = decryptedNotes.find((n: any) => n.isIndex);
    if (index?.payload?.files?.length > 0) {
      // Deduplicate by d-tag — keep only the entry with the latest modified timestamp.
      // This prevents a ghost duplicate when a move optimistic-update races with a relay re-fetch.
      // Use strict > (not >=) so the FIRST-seen entry wins ties rather than the last,
      // preserving the optimistic entry that was added first in the array.
      const latestByD = new Map<string, any>();
      for (const f of index.payload.files) {
        if (!f.d) continue;
        const existing = latestByD.get(f.d);
        if (!existing || (f.modified || 0) > (existing.modified || 0)) {
          latestByD.set(f.d, f);
        }
      }

      // Use vault index manifest for canonical list + titles, excluding tombstones.
      // Use deriveTitleFromPayload so Onyx entries (no title field, path-only) get
      // the correct basename rather than the full path string.
      return Array.from(latestByD.values())
        .filter((f: any) => !deletedDSet.has(f.d))
        .map((f: any) => ({
          tags: [['d', f.d || '']],
          payload: { 
            title: deriveTitleFromPayload(f, f.d),
            path: f.path 
          },
          created_at: f.modified || 0,
        }))
        .sort((a, b) => (b.created_at || 0) - (a.created_at || 0))
        .slice(0, 100);
    }

    // Fallback: scan events (dedup by d-tag), exclude tombstones
    const latestByD = new Map();
    decryptedNotes
      .filter((n: any) => !n.isIndex && (n.payload?.content || n.content))
      .filter((n: any) => {
        const dTag = n.tags?.find((t: string[]) => t[0] === 'd')?.[1];
        return !deletedDSet.has(dTag);
      })
      .forEach((note: any) => {
        const dTag = note.tags?.find((t: string[]) => t[0] === 'd')?.[1] || 'untitled';
        const existing = latestByD.get(dTag);
        if (!existing || (note.created_at || 0) > (existing.created_at || 0)) {
          latestByD.set(dTag, note);
        }
      });

    return Array.from(latestByD.values())
      .sort((a, b) => (b.created_at || 0) - (a.created_at || 0))
      .slice(0, 100);
  }, [decryptedNotes, deletedFiles]);

  const currentNoteTitle = useMemo(() => {
    if (selectedNote) {
      return getNoteTitle(selectedNote);
    }
    if (selectedNoteD) {
      // Find the real note object with payload (title/path)
      const matching = documentList.find((n: any) => {
        const d = n.tags?.find((t: string[]) => t[0] === 'd')?.[1];
        return d === selectedNoteD;
      }) || decryptedNotes.find((n: any) => {
        const d = n.tags?.find((t: string[]) => t[0] === 'd')?.[1];
        return d === selectedNoteD;
      });
      if (matching) return getNoteTitle(matching);
    }
    return 'Untitled Document';
  }, [selectedNote, selectedNoteD, documentList, decryptedNotes, getNoteTitle]);

  // Wikilink resolver: maps [[Note Title]] → /notes/<d-tag> if found in vault, else null.
  // Stable via useCallback so MarkdownRenderer doesn't rebuild its processor on every render.
  const resolveWikilink = useCallback((name: string): string | null => {
    const doc = documentList.find((d: any) =>
      d.payload?.title?.toLowerCase() === name.toLowerCase() ||
      d.payload?.path?.endsWith(`/${name}.md`)
    );
    if (!doc) return null;
    const dTag = doc.tags?.find((t: any) => t[0] === 'd')?.[1];
    return dTag ? `/notes/${dTag}` : null;
  }, [documentList]);

  // Wikilink click handler: opens the target document in-app without a page reload.
  // href is always /notes/<d-tag> (set by resolveWikilink above).
  const handleWikilinkClick = useCallback((href: string) => {
    const dTag = href.replace(/^\/notes\//, '');
    if (!dTag) return;
    const fullNote = decryptedNotes.find((n: any) => {
      const nd = n.tags?.find((t: string[]) => t[0] === 'd')?.[1];
      return nd === dTag;
    });
    if (!fullNote) return;
    setSelectedNote(fullNote);
    const content = fullNote.payload?.content || fullNote.content || '';
    const safeContent = typeof content === 'string' ? content : '';
    setNoteContent(safeContent);
    setNoteIsCorrupted(looksLikeCiphertext(safeContent));
    setSelectedNoteD(dTag);
  }, [decryptedNotes, looksLikeCiphertext]);

  // Build hierarchical tree from paths — mirrors Onyx's FileEntry tree structure.
  // Folders are intermediate path segments; files are leaf nodes.
  // Notes without a path are placed at the root level as file nodes.
  // Empty folders are seeded from the vault index folders[] array so they persist even
  // after all their documents have been moved out.
  const fileTree = useMemo(() => {
    const tree: any = { name: 'Workspace', isFolder: true, children: {}, path: '/' };

    // Seed explicit folder entries from the vault index first so empty folders appear.
    const indexNode = decryptedNotes.find((n: any) => n.isIndex);
    const explicitFolders: string[] = indexNode?.payload?.folders || [];
    for (const folderPath of explicitFolders) {
      const rawPath = folderPath.replace(/^\/+/, '').replace(/\/+$/, '');
      if (!rawPath) continue;
      const parts = rawPath.split('/').filter(Boolean);
      let current = tree;
      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        const currentPath = '/' + parts.slice(0, i + 1).join('/');
        if (!current.children) current.children = {};
        if (!current.children[part]) {
          current.children[part] = {
            name: part.replace(/[-_]/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()),
            isFolder: true,
            path: currentPath,
            note: null,
            children: {},
            dTag: '',
          };
        }
        current = current.children[part];
      }
    }

    // Track which d-tags have already been placed so a stale relay race can never insert
    // the same note twice under two different paths.
    const placedDTags = new Set<string>();

    documentList.forEach((note: any) => {
      const dTag = note.tags?.find((t: any) => t[0] === 'd')?.[1] || '';

      // Skip if we've already placed this d-tag — dedup at the tree level as a safety net.
      if (dTag && placedDTags.has(dTag)) return;
      if (dTag) placedDTags.add(dTag);

      const rawPath = (note.payload?.path || note.path || '').replace(/^\/+/, '').replace(/\/$/, '');

      if (!rawPath) {
        // No path — place at root as a file node using the d-tag as key
        const fallbackKey = dTag || `__nopath_${Math.random()}`;
        // Derive a display name from title or d-tag
        const fallbackName = note.payload?.title
          || (dTag ? dTag.replace(/^doc-/, '').replace(/[-_]/g, ' ').trim() : 'Untitled');
        if (!tree.children[fallbackKey]) {
          tree.children[fallbackKey] = {
            name: fallbackName,
            isFolder: false,
            path: `/__root/${fallbackKey}`,
            note,
            children: {},
            dTag,
          };
        }
        return;
      }

      const parts = rawPath.split('/').filter(Boolean);
      let current = tree;

      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        const isLast = i === parts.length - 1;
        const currentPath = '/' + parts.slice(0, i + 1).join('/');

        if (!current.children) current.children = {};

        if (!current.children[part]) {
          // For leaf file nodes, prefer the note's explicit title over the slug
          const leafName = isLast
            ? (note.payload?.title || part.replace(/\.md$/i, '').replace(/[-_]/g, ' ').trim())
            : part.replace(/\.md$/i, '');
          current.children[part] = {
            name: leafName,
            isFolder: !isLast,
            path: currentPath,
            note: isLast ? note : null,
            children: {},
            dTag: isLast ? dTag : '',
          };
        } else if (isLast && current.children[part].isFolder) {
          // Edge case: same segment was previously an intermediate folder,
          // now we're treating it as a file. Attach note data but keep isFolder.
          current.children[part].note = note;
          current.children[part].dTag = dTag;
        }

        current = current.children[part];
      }
    });

    return tree;
  }, [documentList]);

  const toggleFolder = useCallback((path: string) => {
    setExpandedPaths(prev => 
      prev.includes(path) 
        ? prev.filter(p => p !== path) 
        : [...prev, path]
    );
  }, []);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Flatten tree to a list of sortable FILE IDs for SortableContext.
  // Folders are handled by useDroppable separately and are NOT in this list.
  const flatTreeIds = useMemo(() => {
    const ids: string[] = [];
    const flatten = (node: any) => {
      const children = Object.values(node.children || {}) as any[];
      const sorted = children.sort((a: any, b: any) => {
        if (a.isFolder && !b.isFolder) return -1;
        if (!a.isFolder && b.isFolder) return 1;
        return a.name.localeCompare(b.name);
      });
      for (const child of sorted) {
        if (!child.isFolder) {
          ids.push(`file:${child.dTag}`);
        }
        if (child.isFolder && expandedPaths.includes(child.path)) {
          flatten(child);
        }
      }
    };
    flatten(fileTree);
    return ids;
  }, [fileTree, expandedPaths]);

  // Move a file to a new folder — mirrors Onyx's moveFile():
  // Re-publishes the kind-30800 event with the updated path (preserving content, d-tag, and version chain),
  // then updates the vault index entry for that d-tag to the new path.
  // No separate optimistic-update state is needed; the d-tag is stable so the tree updates naturally.
  const moveFileToFolder = useCallback(async (dTag: string, newFolderPath: string) => {
    if (!user?.signer?.nip44 || !user.pubkey) return;

    const currentNotes = decryptedNotesRef.current;

    const note = currentNotes.find((n: any) => {
      const nd = n.tags?.find((t: string[]) => t[0] === 'd')?.[1];
      return nd === dTag;
    });
    if (!note) return;

    const now = Math.floor(Date.now() / 1000);
    const title = note.payload?.title || getNoteTitle(note);
    const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'untitled';

    // Build the new path correctly for every target location:
    //   /          → /<slug>.md        (root)
    //   /notes     → /notes/<slug>.md
    //   /projects  → /projects/<slug>.md
    const cleanFolder = newFolderPath.replace(/^\/+|\/+$/g, '');
    const candidateMovePath = cleanFolder ? `/${cleanFolder}/${slug}.md` : `/${slug}.md`;
    // Ensure the path is unique in the target folder to avoid tree collisions
    const moveExistingFiles: any[] = currentNotes.find((n: any) => n.isIndex && n.payload?.files)?.payload?.files || [];
    const movePathTaken = moveExistingFiles.some((f: any) => f.path === candidateMovePath && f.d !== dTag);
    const moveUniqueSuffix = movePathTaken ? `-${dTag.slice(-6)}` : '';
    const newPath = cleanFolder ? `/${cleanFolder}/${slug}${moveUniqueSuffix}.md` : `/${slug}${moveUniqueSuffix}.md`;

    // Use the current editor content if this is the note being viewed,
    // otherwise fall back to the last-decrypted payload content (Onyx behaviour).
    const isCurrentNote = dTag === selectedNoteD;
    const content: string = isCurrentNote
      ? noteContent
      : (note.payload?.content || '');

    const newPayload = {
      ...note.payload,
      path: newPath,
      content,
      modified: now,
      version: (note.payload?.version || 0) + 1,
      previousEventId: note.id || undefined,
    };

    // Build the updated vault index files array — replace (or add) the entry for this d-tag.
    const existingIndex = currentNotes.find((n: any) => n.isIndex && n.payload?.files);
    const existingFiles: any[] = existingIndex?.payload?.files || [];

    // Deduplicate by d-tag first (heal any pre-existing corruption), then update this file's entry.
    const deduped = new Map<string, any>();
    for (const f of existingFiles) {
      if (!f.d) continue;
      const prev = deduped.get(f.d);
      if (!prev || (f.modified || 0) >= (prev.modified || 0)) deduped.set(f.d, f);
    }
    deduped.set(dTag, { ...(deduped.get(dTag) || {}), d: dTag, path: newPath, title, modified: now });
    const updatedFiles = Array.from(deduped.values());

    // Optimistic update — patch in-memory state so the tree re-renders immediately without
    // waiting for relay round-trip. No pendingMoves ref needed because the d-tag is stable.
    // Advance vault timestamp guard (now+1 matches the vault index created_at we'll publish)
    // so a stale relay re-fetch cannot overwrite this optimistic move state.
    latestVaultTimestampRef.current = now + 1;
    const patchedNotes = currentNotes.map((n: any) => {
      const nd = n.tags?.find((t: string[]) => t[0] === 'd')?.[1];
      if (nd === dTag) return { ...n, created_at: now, payload: newPayload };
      if (n.isIndex && n.payload?.files) return { ...n, created_at: now + 1, payload: { ...n.payload, files: updatedFiles, updated: now } };
      return n;
    });
    setDecryptedNotes(patchedNotes);

    // If the note is currently open in the editor, keep selectedNote in sync.
    if (isCurrentNote && selectedNote) {
      setSelectedNote({ ...selectedNote, payload: newPayload });
    }

    // Auto-expand target folder so the moved note is visible.
    if (newFolderPath !== '/') {
      setExpandedPaths(prev => prev.includes(newFolderPath) ? prev : [...prev, newFolderPath]);
    }

    try {
      // 1. Publish updated kind-30800 with full content + new path (same d-tag).
      const fileEncrypted = await user.signer.nip44.encrypt(user.pubkey, JSON.stringify(newPayload));
      const publishedMoveEvent = await publishEvent({
        kind: KIND_FILE,
        created_at: now,
        tags: [['d', dTag], ['encrypted', 'nip44']],
        content: fileEncrypted,
      } as any);
      const realMoveEventId: string = publishedMoveEvent?.id || `move-file-${dTag}-${now}`;

      // Update the vault entry with the real event ID now that we have it
      deduped.set(dTag, { ...(deduped.get(dTag) || {}), eventId: realMoveEventId, d: dTag, path: newPath, title, modified: now, version: newPayload.version });
      const updatedFilesWithEventId = Array.from(deduped.values());

      // 2. Publish updated vault index (kind-30801) with the corrected path for this file.
      const existingFoldersForMove: string[] = existingIndex?.payload?.folders || [];
      const indexPayload = {
        name: "Nostrion Workspace",
        description: "Private encrypted documents synced via Nostr",
        files: updatedFilesWithEventId,
        folders: existingFoldersForMove,
        deleted: existingIndex?.payload?.deleted || [],
        updated: now,
      };
      const indexEncrypted = await user.signer.nip44.encrypt(user.pubkey, JSON.stringify(indexPayload));
      await publishEvent({
        kind: KIND_VAULT_INDEX,
        created_at: now + 1,
        tags: [['d', activeVaultDRef.current], ['encrypted', 'nip44']],
        content: indexEncrypted,
      } as any);

      // 3. Update the TanStack Query cache directly with the new encrypted events so the decrypt
      //    effect sees the correct data on the next run. This prevents a relay round-trip from
      //    returning the old vault index and overwriting the optimistic update.
      queryClient.setQueryData(
        ['nostrion-notes', user.pubkey],
        (old: any[] | undefined) => {
          if (!old) return old;
          // Replace the matching file event and vault index event with the new versions.
          const filtered = old.filter((e: any) => {
            if (e.kind === KIND_FILE) {
              const ed = e.tags?.find((t: string[]) => t[0] === 'd')?.[1];
              return ed !== dTag; // drop old version of this file
            }
            if (e.kind === KIND_VAULT_INDEX) return false; // drop old vault index
            return true;
          });
          // Inject the new encrypted events (with synthetic ids so the cache is valid)
          filtered.push({
            kind: KIND_FILE,
            created_at: now,
            tags: [['d', dTag], ['encrypted', 'nip44']],
            content: fileEncrypted,
            id: `move-file-${dTag}-${now}`,
            pubkey: user.pubkey,
            sig: '',
          });
          filtered.push({
            kind: KIND_VAULT_INDEX,
            created_at: now + 1,
            tags: [['d', activeVaultDRef.current], ['encrypted', 'nip44']],
            content: indexEncrypted,
            id: `move-index-${now}`,
            pubkey: user.pubkey,
            sig: '',
          });
          return filtered;
        }
      );

      toast({ title: "File moved", description: `Moved to ${newFolderPath === '/' ? 'root' : newFolderPath}` });
    } catch (err) {
      console.error('Failed to move file:', err);
      // Revert optimistic update on failure.
      setDecryptedNotes(currentNotes);
      if (isCurrentNote && selectedNote) setSelectedNote(selectedNote);
      toast({ title: "Move failed", description: "Please try again.", variant: "destructive" });
    }
  }, [user, decryptedNotesRef, getNoteTitle, publishEvent, toast, KIND_FILE, KIND_VAULT_INDEX, selectedNoteD, noteContent, selectedNote, queryClient]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  }, []);

  const handleDragOver = useCallback((event: DragOverEvent) => {
    setOverId(event.over ? String(event.over.id) : null);
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    setOverId(null);
    if (!over || active.id === over.id) return;

    const activeIdStr = String(active.id);
    const overIdStr = String(over.id);

    // Only support dragging files for now (folder reordering is complex)
    if (!activeIdStr.startsWith('file:')) return;

    const dTag = activeIdStr.replace('file:', '');

    // Determine target folder path
    let targetFolder: string | null = null;
    if (overIdStr.startsWith('folder:')) {
      // Dropped directly onto a folder — use that folder
      targetFolder = overIdStr.replace('folder:', '');
    } else if (overIdStr.startsWith('file:')) {
      // Dropped onto another file — move to the same folder as that file
      const targetDTag = overIdStr.replace('file:', '');
      const targetNote = decryptedNotesRef.current.find((n: any) =>
        n.tags?.find((t: string[]) => t[0] === 'd')?.[1] === targetDTag
      );
      if (targetNote?.payload?.path) {
        const parts = targetNote.payload.path.replace(/^\/+/, '').split('/');
        parts.pop(); // strip filename
        targetFolder = parts.length > 0 ? '/' + parts.join('/') : '/';
      } else {
        targetFolder = '/';
      }
    }

    if (targetFolder !== null) {
      moveFileToFolder(dTag, targetFolder);
    }
  }, [decryptedNotesRef, moveFileToFolder]);

  // Delete a file — mirrors Onyx's deleteFile():
  // Removes the entry from vault.data.files[] and adds a tombstone to vault.data.deleted[].
  // The underlying kind 30800 event is never overwritten (Nostr is append-only).
  // Restore = remove from deleted[], re-add to files[].
  //
  // IMPORTANT: We update the TanStack cache directly (not invalidateQueries) so the
  // relay cannot resurface the old vault index before relay propagation catches up.
  // This prevents deleted files from reappearing in the sidebar after deletion.
  const deleteFile = useCallback(async (dTag: string) => {
    if (!user?.signer?.nip44 || !user.pubkey) return;

    const now = Math.floor(Date.now() / 1000);
    // Read from ref to get freshest state — safe even if called in rapid succession
    const currentNotes = decryptedNotesRef.current;
    const existingIndex = currentNotes.find((n: any) => n.isIndex && n.payload?.files);
    const currentFiles: any[] = existingIndex?.payload?.files || [];
    const currentDeleted: any[] = existingIndex?.payload?.deleted || [];
    const currentFolders: string[] = existingIndex?.payload?.folders || [];

    // Find the file entry to build the tombstone
    const fileEntry = currentFiles.find((f: any) => f.d === dTag);
    const title = fileEntry?.title || 'Untitled';
    const path = fileEntry?.path || `/notes/${dTag}.md`;
    const lastEventId = fileEntry?.eventId;

    // Remove from files[], add tombstone to deleted[]
    const updatedFiles = currentFiles.filter((f: any) => f.d !== dTag);
    const tombstone = { d: dTag, path, title, deletedAt: now, ...(lastEventId ? { lastEventId } : {}) };
    const updatedDeleted = [...currentDeleted.filter((t: any) => t.d !== dTag), tombstone];

    const indexPayload = {
      name: "Nostrion Workspace",
      description: "Private encrypted documents synced via Nostr",
      files: updatedFiles,
      folders: currentFolders, // always preserved
      deleted: updatedDeleted,
      updated: now,
    };

    try {
      const indexEncrypted = await user.signer.nip44.encrypt(user.pubkey, JSON.stringify(indexPayload));
      await publishEvent({
        kind: KIND_VAULT_INDEX, created_at: now,
        tags: [['d', activeVaultDRef.current], ['encrypted', 'nip44']],
        content: indexEncrypted,
      } as any);

      // ── Optimistic local state update ──
      // Advance the vault timestamp guard so the decryptNotes effect doesn't overwrite
      // this optimistic state when the (potentially stale) relay vault re-arrives.
      latestVaultTimestampRef.current = now;
      setDeletedFiles(prev => [...prev.filter(f => f.d !== dTag), tombstone]);
      setDecryptedNotes(prev => prev.map((n: any) => {
        if (!n.isIndex || !n.payload?.files) return n;
        return { ...n, created_at: now, payload: { ...n.payload, files: updatedFiles, folders: currentFolders, deleted: updatedDeleted, updated: now } };
      }));

      // ── Update TanStack cache directly ── (do NOT invalidateQueries here)
      // invalidateQueries would trigger a relay re-fetch that could return the old vault
      // index before relay propagation, causing the deleted file to reappear.
      queryClient.setQueryData(
        ['nostrion-notes', user.pubkey],
        (old: any[] | undefined) => {
          if (!old) return old;
          // Remove old vault index, inject new encrypted version
          const filtered = old.filter((e: any) => e.kind !== KIND_VAULT_INDEX);
          filtered.push({
            kind: KIND_VAULT_INDEX,
            created_at: now,
            tags: [['d', activeVaultDRef.current], ['encrypted', 'nip44']],
            content: indexEncrypted,
            id: `delete-index-${dTag}-${now}`,
            pubkey: user.pubkey,
            sig: '',
          });
          return filtered;
        }
      );

      // If currently viewing the deleted doc, clear selection
      if (selectedNoteD === dTag) {
        setSelectedNote(null);
        setSelectedNoteD(null);
        setNoteContent('');
      }

      toast({ title: "Document deleted", description: "Moved to trash. Restore it from Settings → Sync." });
    } catch (err) {
      console.error('Failed to delete file:', err);
      toast({ title: "Delete failed", variant: "destructive" });
    }
  }, [user, decryptedNotesRef, publishEvent, queryClient, toast, selectedNoteD, KIND_VAULT_INDEX]);

  // Scan Nostr relays for all kind-30800 events and surface any that aren't
  // currently in the active documentList or the deleted[] tombstone list.
  // Mirrors Onyx's "Scan for recoverable files" flow.
  const scanForRecoverableFiles = useCallback(async () => {
    if (!user?.signer?.nip44 || !user.pubkey || !nostr) return;

    setScanLoading(true);
    setScanMessage('Scanning your relays for unindexed documents…');
    setScannedRecoverableFiles([]);

    try {
      // Fetch ALL kind-30800 events for this user — no stale-time limit
      const events = await nostr.query([{
        kinds: [KIND_FILE],
        authors: [user.pubkey],
        limit: 500,
      }]);

      // Collect active dTags (documents currently visible in the workspace)
      const activeDTags = new Set(
        documentList.map((n: any) => n.tags?.find((t: string[]) => t[0] === 'd')?.[1]).filter(Boolean)
      );

      // Also exclude tombstoned files — they're already shown in the Trash section
      const deletedDSet = new Set(deletedFiles.map(f => f.d));

      // Deduplicate events by d-tag — keep only the latest (highest created_at)
      const latestByD = new Map<string, any>();
      for (const ev of events) {
        const dTag = ev.tags?.find((t: string[]) => t[0] === 'd')?.[1];
        if (!dTag) continue;
        const existing = latestByD.get(dTag);
        if (!existing || ev.created_at > existing.created_at) {
          latestByD.set(dTag, ev);
        }
      }

      // Decrypt each event that isn't active or already tombstoned
      const recoverable: { dTag: string; title: string; path: string; modifiedAt: number }[] = [];
      for (const [dTag, ev] of latestByD.entries()) {
        if (activeDTags.has(dTag)) continue;   // already in workspace
        if (deletedDSet.has(dTag)) continue;   // already in Trash

        try {
          const decrypted = await user.signer.nip44.decrypt(user.pubkey, ev.content);
          const payload = JSON.parse(decrypted);
          recoverable.push({
            dTag,
            title: String(payload?.title || payload?.path?.split('/').pop()?.replace(/\.md$/, '') || 'Untitled'),
            path: payload?.path || `/${dTag}.md`,
            modifiedAt: payload?.modified || ev.created_at || 0,
          });
        } catch {
          // Can't decrypt — skip (wrong key or corrupt event)
        }
      }

      // Sort newest first
      recoverable.sort((a, b) => b.modifiedAt - a.modifiedAt);
      setScannedRecoverableFiles(recoverable);
      setScanMessage(
        recoverable.length > 0
          ? `Found ${recoverable.length} recoverable document${recoverable.length === 1 ? '' : 's'}.`
          : 'No additional recoverable documents found.'
      );
    } catch (err) {
      console.error('Scan failed:', err);
      setScanMessage('Scan failed. Check your relay connections and try again.');
    } finally {
      setScanLoading(false);
    }
  }, [user, nostr, documentList, deletedFiles, KIND_FILE]);

  // Restore a file — mirrors Onyx's restore flow:
  // Removes the tombstone from deleted[], re-adds the entry to files[].
  // Works for both Trash items (tombstoned) and orphaned relay files (scan results).
  const restoreFile = useCallback(async (item: { dTag: string; title: string; path: string; modifiedAt: number }) => {
    if (!user?.signer?.nip44 || !user.pubkey) return;

    setRecoveringScannedD(item.dTag);
    const now = Math.floor(Date.now() / 1000);
    // Use ref for freshest state
    const currentNotes = decryptedNotesRef.current;
    const existingIndex = currentNotes.find((n: any) => n.isIndex && n.payload?.files);
    const currentFiles: any[] = existingIndex?.payload?.files || [];
    const currentDeleted: any[] = existingIndex?.payload?.deleted || [];
    const currentFolders: string[] = existingIndex?.payload?.folders || [];

    const entry = { d: item.dTag, path: item.path, title: item.title, modified: item.modifiedAt || now };

    // Add back to files[], remove from deleted[]
    const alreadyInFiles = currentFiles.some((f: any) => f.d === item.dTag);
    const updatedFiles = alreadyInFiles
      ? currentFiles.map((f: any) => f.d === item.dTag ? entry : f)
      : [...currentFiles, entry];
    const updatedDeleted = currentDeleted.filter((t: any) => t.d !== item.dTag);

    const indexPayload = {
      name: "Nostrion Workspace",
      description: "Private encrypted documents synced via Nostr",
      files: updatedFiles,
      folders: currentFolders,
      deleted: updatedDeleted,
      updated: now,
    };

    try {
      const indexEncrypted = await user.signer.nip44.encrypt(user.pubkey, JSON.stringify(indexPayload));
      await publishEvent({
        kind: KIND_VAULT_INDEX, created_at: now,
        tags: [['d', activeVaultDRef.current], ['encrypted', 'nip44']],
        content: indexEncrypted,
      } as any);

      // ── Optimistic local state update ──
      latestVaultTimestampRef.current = now;
      setDeletedFiles(prev => prev.filter(f => f.d !== item.dTag));
      setDecryptedNotes(prev => prev.map((n: any) => {
        if (!n.isIndex || !n.payload) return n;
        const existingInFiles = (n.payload.files || []).some((f: any) => f.d === item.dTag);
        const updFiles = existingInFiles
          ? (n.payload.files || []).map((f: any) => f.d === item.dTag ? entry : f)
          : [...(n.payload.files || []), entry];
        return { ...n, created_at: now, payload: { ...n.payload, files: updFiles, folders: currentFolders, deleted: updatedDeleted, updated: now } };
      }));

      // ── Update TanStack cache directly ── (no invalidateQueries)
      queryClient.setQueryData(
        ['nostrion-notes', user.pubkey],
        (old: any[] | undefined) => {
          if (!old) return old;
          const filtered = old.filter((e: any) => e.kind !== KIND_VAULT_INDEX);
          filtered.push({
            kind: KIND_VAULT_INDEX,
            created_at: now,
            tags: [['d', activeVaultDRef.current], ['encrypted', 'nip44']],
            content: indexEncrypted,
            id: `restore-index-${item.dTag}-${now}`,
            pubkey: user.pubkey,
            sig: '',
          });
          return filtered;
        }
      );

      // Remove from scan results if present
      setScannedRecoverableFiles(prev => prev.filter(f => f.dTag !== item.dTag));

      toast({ title: "Document restored", description: `"${item.title}" is back in your workspace.` });
    } catch (err) {
      console.error('Restore failed:', err);
      toast({ title: "Restore failed", variant: "destructive" });
    } finally {
      setRecoveringScannedD(null);
    }
  }, [user, decryptedNotesRef, publishEvent, queryClient, toast, KIND_VAULT_INDEX]);

  // Create a new folder — records it in the vault index folders[] array only.
  // No placeholder document is created; the folder exists independently of any files.
  // Always preserves deleted[] so tombstones survive folder creation.
  const createNewFolder = useCallback(async (folderName: string, parentPath = '/') => {
    if (!user?.signer?.nip44 || !user.pubkey) {
      toast({ title: "Not logged in", description: "Login with Nostr to create folders", variant: "destructive" });
      return;
    }

    const cleanParent = parentPath === '/' ? '' : parentPath.replace(/^\/+|\/+$/g, '');
    const folderSlug = folderName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'folder';
    const folderPath = cleanParent ? `/${cleanParent}/${folderSlug}` : `/${folderSlug}`;
    const now = Math.floor(Date.now() / 1000);

    try {
      // Always read from ref for freshest state
      const currentNotes = decryptedNotesRef.current;
      const existingIndex = currentNotes.find((n: any) => n.isIndex && n.payload?.files);
      const existingFiles: any[] = existingIndex?.payload?.files || [];
      const existingFolders: string[] = existingIndex?.payload?.folders || [];
      const existingDeleted: any[] = existingIndex?.payload?.deleted || [];

      // Don't add duplicates
      if (existingFolders.includes(folderPath)) {
        toast({ title: `Folder already exists`, variant: "destructive" });
        return;
      }

      const updatedFolders = [...existingFolders, folderPath];

      const indexPayload = {
        name: "Nostrion Workspace",
        description: "Private encrypted documents synced via Nostr",
        files: existingFiles,
        folders: updatedFolders,
        deleted: existingDeleted, // always preserved
        updated: now,
      };
      const indexEncrypted = await user.signer.nip44.encrypt(user.pubkey, JSON.stringify(indexPayload));
      await publishEvent({
        kind: KIND_VAULT_INDEX, created_at: now,
        tags: [['d', activeVaultDRef.current], ['encrypted', 'nip44']],
        content: indexEncrypted,
      } as any);

      // Optimistic local state update
      setDecryptedNotes(prev => prev.map((n: any) => {
        if (!n.isIndex) return n;
        return { ...n, payload: { ...n.payload, folders: updatedFolders, deleted: existingDeleted, updated: now } };
      }));

      // Update TanStack cache directly — no invalidateQueries
      queryClient.setQueryData(
        ['nostrion-notes', user.pubkey],
        (old: any[] | undefined) => {
          if (!old) return old;
          const filtered = old.filter((e: any) => e.kind !== KIND_VAULT_INDEX);
          filtered.push({ kind: KIND_VAULT_INDEX, created_at: now, tags: [['d', activeVaultDRef.current], ['encrypted', 'nip44']], content: indexEncrypted, id: `create-folder-index-${now}`, pubkey: user.pubkey, sig: '' });
          return filtered;
        }
      );

      // Auto-expand the new folder
      setExpandedPaths(prev => prev.includes(folderPath) ? prev : [...prev, folderPath]);
      toast({ title: `Folder "${folderName}" created` });
    } catch (err) {
      console.error('Failed to create folder:', err);
      toast({ title: "Error creating folder", variant: "destructive" });
    }
  }, [user, publishEvent, queryClient, toast, decryptedNotesRef, KIND_VAULT_INDEX]);

  const handleNewFolderConfirm = useCallback(() => {
    const name = newFolderNameInput.trim() || 'New Folder';
    createNewFolder(name, newFolderParentPath);
    setShowNewFolderModal(false);
    setNewFolderNameInput('');
    setNewFolderParentPath('/');
  }, [newFolderNameInput, newFolderParentPath, createNewFolder]);

  // Rename folder — rewrites the path segment on every document inside that folder
  const renameFolder = useCallback(async (oldFolderPath: string, newName: string) => {
    if (!user?.signer?.nip44 || !user.pubkey) {
      toast({ title: "Not logged in", variant: "destructive" });
      return;
    }

    const newSlug = newName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'folder';

    // Build the new folder path by replacing just the last segment of oldFolderPath
    const oldSegments = oldFolderPath.replace(/^\/+/, '').split('/');
    oldSegments[oldSegments.length - 1] = newSlug;
    const newFolderPath = '/' + oldSegments.join('/');

    if (newFolderPath === oldFolderPath) return;

    // Find all documents whose path starts with oldFolderPath
    const affected = decryptedNotes.filter((n: any) => {
      if (n.isIndex) return false;
      const p: string = n.payload?.path || '';
      return p === oldFolderPath || p.startsWith(oldFolderPath + '/') || p.startsWith(oldFolderPath.replace(/^\//,'') + '/');
    });

    const now = Math.floor(Date.now() / 1000);

    // Optimistically update local state
    const updatedNotes = decryptedNotes.map((n: any) => {
      if (n.isIndex) return n;
      const p: string = n.payload?.path || '';
      if (!p.startsWith(oldFolderPath)) return n;
      const newPath = p.replace(oldFolderPath, newFolderPath);
      return { ...n, payload: { ...n.payload, path: newPath, modified: now } };
    });
    setDecryptedNotes(updatedNotes);

    // Expand the new folder path
    setExpandedPaths(prev => {
      const next = prev.filter(p => !p.startsWith(oldFolderPath));
      next.push(newFolderPath);
      return next;
    });

    try {
      // Republish each affected document with the updated path
      for (const note of affected) {
        const dTag = note.tags?.find((t: string[]) => t[0] === 'd')?.[1];
        if (!dTag) continue;
        const oldPath: string = note.payload?.path || '';
        const newPath = oldPath.replace(oldFolderPath, newFolderPath);
        const newPayload = { ...note.payload, path: newPath, modified: now };
        const encrypted = await user.signer.nip44.encrypt(user.pubkey, JSON.stringify(newPayload));
        await publishEvent({
          kind: KIND_FILE, created_at: now,
          tags: [['d', dTag], ['encrypted', 'nip44']],
          content: encrypted,
        } as any);
      }

      // Rebuild vault index with updated paths — always preserve deleted[]
      const existingIndex = updatedNotes.find((n: any) => n.isIndex && n.payload?.files);
      const files = (existingIndex?.payload?.files || []).map((f: any) => {
        const p: string = f.path || '';
        if (!p.startsWith(oldFolderPath)) return f;
        return { ...f, path: p.replace(oldFolderPath, newFolderPath), modified: now };
      });
      // Also update the folders[] list so the renamed folder persists when empty
      const existingFolders: string[] = existingIndex?.payload?.folders || [];
      const updatedFolders = existingFolders.map((fp: string) =>
        fp === oldFolderPath || fp.startsWith(oldFolderPath + '/')
          ? fp.replace(oldFolderPath, newFolderPath)
          : fp
      );
      // Always carry forward tombstones
      const existingDeleted: any[] = existingIndex?.payload?.deleted || [];

      const indexPayload = { name: "Nostrion Workspace", description: "Private encrypted documents synced via Nostr", files, folders: updatedFolders, deleted: existingDeleted, updated: now };
      const indexEncrypted = await user.signer.nip44.encrypt(user.pubkey, JSON.stringify(indexPayload));
      await publishEvent({
        kind: KIND_VAULT_INDEX, created_at: now + 1,
        tags: [['d', activeVaultDRef.current], ['encrypted', 'nip44']],
        content: indexEncrypted,
      } as any);

      // Update TanStack cache directly — no invalidateQueries
      queryClient.setQueryData(
        ['nostrion-notes', user.pubkey],
        (old: any[] | undefined) => {
          if (!old) return old;
          const filtered = old.filter((e: any) => e.kind !== KIND_VAULT_INDEX);
          filtered.push({ kind: KIND_VAULT_INDEX, created_at: now + 1, tags: [['d', activeVaultDRef.current], ['encrypted', 'nip44']], content: indexEncrypted, id: `rename-folder-index-${now}`, pubkey: user.pubkey, sig: '' });
          return filtered;
        }
      );

      toast({ title: `Folder renamed to "${newName}"`, description: affected.length > 0 ? `${affected.length} document(s) updated.` : undefined });
    } catch (err) {
      console.error('Failed to rename folder:', err);
      setDecryptedNotes(decryptedNotes); // revert
      toast({ title: "Rename failed", description: "Please try again.", variant: "destructive" });
    }
  }, [user, decryptedNotes, publishEvent, queryClient, toast, KIND_FILE, KIND_VAULT_INDEX]);

  const handleRenameFolderConfirm = useCallback(() => {
    if (renameFolderPath && renameFolderInput.trim()) {
      renameFolder(renameFolderPath, renameFolderInput.trim());
    }
    setShowRenameFolderModal(false);
    setRenameFolderPath(null);
    setRenameFolderInput('');
  }, [renameFolderPath, renameFolderInput, renameFolder]);

  // Sync indicator logic
  const getSyncPill = useMemo(() => {
    switch (syncStatus) {
      case 'pending':
        return {
          text: 'Pending',
          className: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400',
          dotClass: 'bg-amber-500',
          animate: false,
        };
      case 'syncing':
        return {
          text: 'Syncing',
          className: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400',
          dotClass: 'bg-blue-500 animate-spin',
          animate: true,
        };
      default:
        return {
          text: 'Synced',
          className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400',
          dotClass: 'bg-emerald-500',
          animate: true,
        };
    }
  }, [syncStatus]);

  // Decrypt notes when raw data or user changes (full NIP-44 loop)
  useEffect(() => {
    const decryptNotes = async () => {
      if (!rawNotes || !user?.signer?.nip44 || !user.pubkey) {
        setDecryptedNotes([]);
        return;
      }

      // Pre-pass: for KIND_FILE and KIND_VAULT_INDEX, keep only the newest event per d-tag.
      // This mirrors Onyx's fetchVaults() logic and ensures that even if the cache contains
      // multiple versions of the same addressable event (e.g. old relay data racing with an
      // optimistic cache update), only the most recent version is decrypted and used.
      const newestByKindAndD = new Map<string, any>();
      for (const event of rawNotes) {
        if (event.kind !== KIND_FILE && event.kind !== KIND_VAULT_INDEX) continue;
        const dTag = event.tags?.find((t: string[]) => t[0] === 'd')?.[1] ?? '';
        const key = `${event.kind}:${dTag}`;
        const existing = newestByKindAndD.get(key);
        if (!existing || event.created_at > existing.created_at) {
          newestByKindAndD.set(key, event);
        }
      }

      const decrypted = [];
      for (const event of rawNotes) {
        try {
          if (event.kind === KIND_FILE || event.kind === KIND_VAULT_INDEX) {
            // Skip stale versions — only process the newest event for this kind+d-tag combination.
            const dTag = event.tags?.find((t: string[]) => t[0] === 'd')?.[1] ?? '';
            const key = `${event.kind}:${dTag}`;
            if (newestByKindAndD.get(key) !== event) continue;

            // Decrypt with Onyx chunked-format support.
            // Onyx splits large payloads into multiple NIP-44 ciphertexts joined by '.'
            // The first chunk decrypts to {"_chunked":true,"total":N} when chunked.
            const decryptedContent = await decryptOnyxContent(user.signer.nip44, user.pubkey, event.content);
            const payload = JSON.parse(decryptedContent);

            // For vault index events: deduplicate files by d-tag (keep latest modified).
            // Dedup runs unconditionally to heal any corrupted vault index data on the relay.
            if (event.kind === KIND_VAULT_INDEX && Array.isArray(payload.files)) {
              const deduped = new Map<string, any>();
              for (const f of payload.files) {
                if (!f.d) continue;
                const prev = deduped.get(f.d);
                if (!prev || (f.modified || 0) >= (prev.modified || 0)) deduped.set(f.d, f);
              }
              payload.files = Array.from(deduped.values());
            }

            decrypted.push({
              ...event,
              payload,
              isIndex: event.kind === KIND_VAULT_INDEX,
            });

            // Capture the active vault d-tag so all writes go to the same vault
            // (which may be a UUID from Onyx rather than 'default-vault').
            if (event.kind === KIND_VAULT_INDEX) {
              const vaultD = event.tags?.find((t: string[]) => t[0] === 'd')?.[1];
              if (vaultD) activeVaultDRef.current = vaultD;
            }
          }
        } catch (err) {
          // Cannot decrypt — skip entirely. Showing raw ciphertext or using this
          // event as a document would be confusing and incorrect. Events encrypted
          // with a different scheme (e.g. NIP-04 instead of NIP-44) or a different
          // key fall here. They will not appear in the sidebar or editor.
          console.warn('Skipping undecryptable event', event.id, err);
        }
      }
      // Sync deleted[] tombstones from vault index — mirrors Onyx's vault.data.deleted[]
      // We merge rather than replace: if the relay returns a stale vault index (before our
      // delete has propagated), we must NOT drop tombstones we already know about locally.
      const indexEvent = decrypted.find((n: any) => n.isIndex);
      const incomingVaultTimestamp: number = indexEvent?.created_at || 0;

      // Only apply the relay vault if it is newer than what we already have.
      // If the relay returns an older vault (propagation lag after delete/move), skip it
      // to preserve the optimistic state we injected via setQueryData.
      if (incomingVaultTimestamp >= latestVaultTimestampRef.current) {
        latestVaultTimestampRef.current = incomingVaultTimestamp;
        setDecryptedNotes(decrypted);

        if (indexEvent?.payload?.deleted && Array.isArray(indexEvent.payload.deleted)) {
          const incomingTombstones = indexEvent.payload.deleted
            .filter((t: any) => t.d || t.path) // Onyx uses path-only; Nostrion uses d
            .map((t: any) => {
              // Onyx tombstones only have { path, deletedAt, lastEventId } — no d or title.
              // Derive a stable d-tag from the path so we can track it locally.
              const dTag: string = t.d || `onyx-path-${(t.path || '').replace(/[^a-z0-9]/gi, '-')}`;
              return {
                d: dTag,
                path: t.path || `/notes/${dTag}.md`,
                title: t.title || 'Deleted document',
                deletedAt: t.deletedAt || 0,
                lastEventId: t.lastEventId,
              };
            });

          // Merge: keep any locally-known tombstones that aren't in the relay's list yet.
          // This prevents a stale relay vault from resurrecting a just-deleted note.
          setDeletedFiles(prev => {
            const merged = new Map<string, { d: string; path: string; title: string; deletedAt: number; lastEventId?: string }>();
            // Start with incoming (relay) tombstones
            for (const t of incomingTombstones) merged.set(t.d, t);
            // Overlay any local tombstones that are newer (i.e. not yet on the relay)
            for (const t of prev) {
              const existing = merged.get(t.d);
              if (!existing || t.deletedAt > existing.deletedAt) merged.set(t.d, t);
            }
            return Array.from(merged.values());
          });
        }
      } else {
        // Stale relay vault — don't overwrite decryptedNotes, but still set it for
        // the initial load case where decryptedNotes is empty.
        setDecryptedNotes(prev => {
          if (prev.length === 0) return decrypted;
          return prev;
        });
      }

      // Vault is fully loaded and decrypted — mark as synced
      setSyncStatus('synced');


    };

    decryptNotes();
  }, [rawNotes, user]);

  // Self-healing effect: if the vault index has duplicate d-tag entries (corrupted by old move bugs),
  // republish a clean deduplicated version to the relay once per session.
  const hasHealedVault = useRef(false);
  useEffect(() => {
    if (hasHealedVault.current) return;
    if (!user?.signer?.nip44 || !user.pubkey || !publishEvent) return;

    const indexEvent = decryptedNotes.find((n: any) => n.isIndex);
    if (!indexEvent?.payload?.files) return;

    // Check raw vault event for duplicate d-tags
    const rawVaultEvent = rawNotes?.find((e: any) => e.kind === KIND_VAULT_INDEX);
    if (!rawVaultEvent) return;

    // Count unique d-tags in the raw (already-deduped-in-memory) files vs what we have
    const cleanFiles = indexEvent.payload.files as any[];
    const uniqueDTags = new Set(cleanFiles.map((f: any) => f.d).filter(Boolean));

    // If the raw event has more files than unique d-tags we know there were duplicates
    // We detect this by re-checking the rawNotes vault event file count
    (async () => {
      try {
        const rawDecrypted = await user.signer.nip44!.decrypt(user.pubkey, rawVaultEvent.content);
        const rawPayload = JSON.parse(rawDecrypted);
        if (!Array.isArray(rawPayload.files)) return;

        const rawCount = rawPayload.files.filter((f: any) => f.d).length;
        if (rawCount <= uniqueDTags.size) return; // no duplicates, nothing to heal

        console.log(`[Nostrion] Healing vault index: ${rawCount} raw entries → ${uniqueDTags.size} unique d-tags. Republishing clean index.`);
        hasHealedVault.current = true;

        const now = Math.floor(Date.now() / 1000);
        const cleanPayload = { ...rawPayload, files: cleanFiles, updated: now };
        const cleanEncrypted = await user.signer.nip44!.encrypt(user.pubkey, JSON.stringify(cleanPayload));
        await publishEvent({
          kind: KIND_VAULT_INDEX,
          created_at: now,
          tags: [['d', activeVaultDRef.current], ['encrypted', 'nip44']],
          content: cleanEncrypted,
        } as any);
        console.log('[Nostrion] Clean vault index published to relay.');
      } catch (err) {
        console.warn('[Nostrion] Vault self-heal failed:', err);
      }
    })();
  }, [decryptedNotes, rawNotes, user, publishEvent, KIND_VAULT_INDEX]);

  // Simple save function — used by handleToggleTask when auto-sync is off.
  // Only publishes kind-30800; does NOT touch the vault index (30801).
  // Uses decryptedNotesRef so it always reads the freshest state.
  const saveNoteToNostr = useCallback(async (content: string, dTag?: string) => {
    if (!user?.signer?.nip44 || !user.pubkey) {
      console.log('Not logged in or no signer - note saved locally only');
      return;
    }

    const d = dTag || MAIN_NOTE_D;
    const now = Math.floor(Date.now() / 1000);

    const matching = decryptedNotesRef.current.find((n: any) => {
      const noteD = n.tags?.find((t: string[]) => t[0] === 'd')?.[1];
      return noteD === d;
    });

    // Preserve existing path exactly — especially important for Onyx notes which use
    // relative paths like "Onyx Notes/file.md". Never rewrite the path on a plain save.
    const existingPath: string = matching?.payload?.path || '';
    const path: string = existingPath || (() => {
      const derivedTitle = deriveTitleFromPayload(matching?.payload, d);
      const slug = derivedTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'untitled';
      return `/notes/${slug}.md`;
    })();

    // Only include `title` in the payload if the note already has one (Nostrion-created).
    // Onyx notes don't have a title field — don't pollute them with 'Untitled Document'.
    const existingTitle: string | undefined = matching?.payload?.title && typeof matching.payload.title === 'string'
      ? matching.payload.title
      : undefined;

    const payload: Record<string, unknown> = {
      ...(existingTitle ? { title: existingTitle } : {}),
      path,
      content,
      modified: now,
      version: (matching?.payload?.version || 0) + 1,
    };

    try {
      const encrypted = await user.signer.nip44.encrypt(user.pubkey, JSON.stringify(payload));
      await publishEvent({
        kind: KIND_FILE,
        created_at: now,
        tags: [['d', d], ['encrypted', 'nip44']],
        content: encrypted,
      } as any);
      // Update cache directly — no invalidateQueries
      queryClient.setQueryData(
        ['nostrion-notes', user.pubkey],
        (old: any[] | undefined) => {
          if (!old) return old;
          const filtered = old.filter((e: any) => {
            if (e.kind !== KIND_FILE) return true;
            const ed = e.tags?.find((t: string[]) => t[0] === 'd')?.[1];
            return ed !== d;
          });
          filtered.push({ kind: KIND_FILE, created_at: now, tags: [['d', d], ['encrypted', 'nip44']], content: encrypted, id: `save-toggle-${d}-${now}`, pubkey: user.pubkey, sig: '' });
          return filtered;
        }
      );
      console.log('Saved note to Nostr (kind 30800, d=' + d + ')');
    } catch (err) {
      console.error('Encryption failed:', err);
    }
  }, [user, publishEvent, MAIN_NOTE_D, queryClient, decryptedNotesRef, KIND_FILE]);

  // Create new document with unique d-tag and publish encrypted
  const createNewDocument = useCallback(async (title = "Untitled Document") => {
    if (!user?.signer?.nip44 || !user.pubkey) {
      toast({
        title: "Not logged in",
        description: "Login with Nostr to create documents",
        variant: "destructive",
      });
      return;
    }

    const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'untitled';
    const newD = generateUUID();
    const displayTitle = title;
    const defaultContent = `# ${displayTitle}\n\nStart writing your private note here...\n\nAll changes are encrypted with NIP-44 and synced privately via Nostr.`;

    setNoteContent(defaultContent);
    setSelectedNoteD(newD);
    setSyncStatus('pending');

    const now = Math.floor(Date.now() / 1000);
    const payload = {
      title: displayTitle,
      path: `/notes/${slug || newD}.md`,
      content: defaultContent,
      modified: now,
      version: 1,
      contentType: "text/markdown",
    };

    // Optimistic update so title appears immediately
    const optimisticNote = {
      tags: [['d', newD]],
      payload,
      created_at: now,
      isIndex: false,
    };
    setSelectedNote(optimisticNote);

    try {
      const encrypted = await user.signer.nip44.encrypt(
        user.pubkey,
        JSON.stringify(payload)
      );

      const event = {
        kind: 30800,
        created_at: now,
        tags: [
          ['d', newD],
          ['encrypted', 'nip44'],
        ],
        content: encrypted,
      };

      // Capture the real event ID so the vault index entry has a valid eventId for Onyx
      const publishedFileEvent = await publishEvent(event as any);
      const realFileEventId: string = publishedFileEvent?.id || `create-file-${newD}-${now}`;

      // Also update vault index (KIND_VAULT_INDEX) to maintain manifest of all documents
      // Merge with existing index so previous documents are not lost.
      // Always preserve deleted[] and folders[] so no structural data is silently wiped.
      const existingIndex = decryptedNotesRef.current.find((n: any) => n.isIndex && n.payload?.files);
      const existingFiles: any[] = existingIndex?.payload?.files ? [...existingIndex.payload.files] : [];
      const existingFoldersForCreate: string[] = existingIndex?.payload?.folders || [];
      const existingDeletedForCreate: any[] = existingIndex?.payload?.deleted || [];

      // If this is the very first document (no existing vault index), generate a real
      // UUID vault d-tag so new vaults are Onyx-compatible from the start.
      if (!existingIndex && activeVaultDRef.current === 'default-vault') {
        activeVaultDRef.current = generateUUID();
      }

      // Build the vault file entry with Onyx-compatible fields (eventId, SHA-256 checksum, version)
      const contentChecksum = await calculateChecksum(defaultContent);
      const existingIdx = existingFiles.findIndex((f: any) => f.d === newD);
      const newFileEntry = {
        eventId: realFileEventId,
        d: newD,
        path: payload.path,
        title: payload.title || 'Untitled Document',
        checksum: contentChecksum,
        version: 1,
        modified: now,
      };
      if (existingIdx > -1) {
        existingFiles[existingIdx] = newFileEntry;
      } else {
        existingFiles.push(newFileEntry);
      }

      const indexPayload = {
        name: "Nostrion Workspace",
        description: "Private encrypted documents synced via Nostr",
        files: existingFiles,
        folders: existingFoldersForCreate,
        deleted: existingDeletedForCreate, // always carry forward tombstones
        updated: now,
      };
      const indexEncrypted = await user.signer.nip44.encrypt(user.pubkey, JSON.stringify(indexPayload));
      await publishEvent({
        kind: KIND_VAULT_INDEX,
        created_at: now + 1,
        tags: [['d', activeVaultDRef.current], ['encrypted', 'nip44']],
        content: indexEncrypted,
      } as any);

      // Update TanStack cache directly — no invalidateQueries — so relay cannot
      // resurrect stale data before propagation catches up.
      queryClient.setQueryData(
        ['nostrion-notes', user.pubkey],
        (old: any[] | undefined) => {
          const base = old || [];
          const filtered = base.filter((e: any) => e.kind !== KIND_VAULT_INDEX);
          filtered.push(
            { kind: KIND_FILE, created_at: now, tags: [['d', newD], ['encrypted', 'nip44']], content: encrypted, id: `create-file-${newD}-${now}`, pubkey: user.pubkey, sig: '' },
            { kind: KIND_VAULT_INDEX, created_at: now + 1, tags: [['d', activeVaultDRef.current], ['encrypted', 'nip44']], content: indexEncrypted, id: `create-index-${now}`, pubkey: user.pubkey, sig: '' }
          );
          return filtered;
        }
      );

      toast({ title: "New document created", description: "Encrypted and published to Nostr. Vault index updated." });
      console.log('Created new encrypted document with d=' + newD);
    } catch (err) {
      console.error('Failed to create new document:', err);
      toast({
        title: "Error creating document",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  }, [user, publishEvent, queryClient, toast, decryptedNotesRef, KIND_FILE, KIND_VAULT_INDEX]);

  const handleNewDocumentConfirm = useCallback(() => {
    const title = newDocTitleInput.trim() || 'Untitled Document';
    createNewDocument(title);
    setShowNewDocModal(false);
    setNewDocTitleInput('');
  }, [newDocTitleInput, createNewDocument]);

  // Rename document - updates title and path in payload for same d-tag (addressable event).
  // Uses decryptedNotesRef so we always get the freshest state even if called immediately
  // after another operation. Preserves deleted[] and folders[] in the vault index so
  // tombstones and folder structure are never silently wiped.
  const renameDocument = useCallback(async (dTag: string, newTitle: string) => {
    if (!user?.signer?.nip44 || !user.pubkey) {
      toast({ title: "Not logged in", description: "Login with Nostr to rename documents", variant: "destructive" });
      return;
    }

    // Always read from the ref — not stale closure state — so rapid sequences work correctly
    const currentNotes = decryptedNotesRef.current;
    const note = currentNotes.find((n: any) => {
      const nd = n.tags?.find((t: string[]) => t[0] === 'd')?.[1];
      return nd === dTag;
    });

    if (!note) {
      toast({ title: "Document not found", variant: "destructive" });
      return;
    }

    const now = Math.floor(Date.now() / 1000);
    const slug = newTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'untitled';

    // Preserve the existing folder when renaming — only update the filename segment.
    const existingPath: string = note.payload?.path || '';
    const lastSlash = existingPath.lastIndexOf('/');
    const existingFolder = lastSlash > 0 ? existingPath.substring(0, lastSlash) : '';

    // Make the path unique by checking whether another document in the same folder
    // already uses this slug. If so, append a short suffix of the d-tag to avoid
    // two notes mapping to the same path key in the file tree (which causes duplication).
    const existingIndex = currentNotes.find((n: any) => n.isIndex && n.payload?.files);
    const existingFilesForRename: any[] = existingIndex?.payload?.files || [];
    const candidatePath = existingFolder ? `${existingFolder}/${slug}.md` : `/${slug}.md`;
    const pathAlreadyTaken = existingFilesForRename.some((f: any) => f.path === candidatePath && f.d !== dTag);
    const uniqueSuffix = pathAlreadyTaken ? `-${dTag.slice(-6)}` : '';
    const newPath = existingFolder ? `${existingFolder}/${slug}${uniqueSuffix}.md` : `/${slug}${uniqueSuffix}.md`;

    const newPayload = {
      ...note.payload,
      title: newTitle,
      path: newPath,
      modified: now,
    };

    // ── Optimistic update ── Apply before any network calls so the UI reflects
    // the rename immediately and any pending auto-save debounce reads the new title.
    const patchedNotes = currentNotes.map((n: any) => {
      const nd = n.tags?.find((t: string[]) => t[0] === 'd')?.[1];
      if (nd === dTag) return { ...n, payload: newPayload };
      return n;
    });
    setDecryptedNotes(patchedNotes);
    if (selectedNoteD === dTag) {
      setSelectedNote((prev: any) => prev ? { ...prev, payload: newPayload } : prev);
    }

    try {
      // 1. Publish updated kind-30800 with new title/path (same d-tag).
      const fileEncrypted = await user.signer.nip44.encrypt(user.pubkey, JSON.stringify(newPayload));
      const publishedRenameEvent = await publishEvent({
        kind: KIND_FILE,
        created_at: now,
        tags: [['d', dTag], ['encrypted', 'nip44']],
        content: fileEncrypted,
      } as any);
      const realRenameEventId: string = publishedRenameEvent?.id || `rename-file-${dTag}-${now}`;

      // 2. Publish updated vault index — preserving deleted[] and folders[].
      const existingIndex = patchedNotes.find((n: any) => n.isIndex && n.payload?.files);
      const existingFiles: any[] = existingIndex?.payload?.files || [];
      const existingDeleted: any[] = existingIndex?.payload?.deleted || [];
      const existingFolders: string[] = existingIndex?.payload?.folders || [];

      // Deduplicate files by d-tag, then update this file's entry with the real eventId.
      const deduped = new Map<string, any>();
      for (const f of existingFiles) {
        if (!f.d) continue;
        const prev = deduped.get(f.d);
        if (!prev || (f.modified || 0) >= (prev.modified || 0)) deduped.set(f.d, f);
      }
      deduped.set(dTag, {
        ...(deduped.get(dTag) || {}),
        eventId: realRenameEventId,
        d: dTag,
        path: newPath,
        title: newTitle,
        modified: now,
        version: newPayload.version || (note.payload?.version || 0) + 1,
      });

      const indexPayload = {
        name: "Nostrion Workspace",
        description: "Private encrypted documents synced via Nostr",
        files: Array.from(deduped.values()),
        folders: existingFolders,
        deleted: existingDeleted, // ← always carry forward tombstones
        updated: now,
      };
      const indexEncrypted = await user.signer.nip44.encrypt(user.pubkey, JSON.stringify(indexPayload));
      await publishEvent({
        kind: KIND_VAULT_INDEX,
        created_at: now + 1,
        tags: [['d', activeVaultDRef.current], ['encrypted', 'nip44']],
        content: indexEncrypted,
      } as any);

      // 3. Update TanStack cache directly — prevents relay re-fetch from resurrecting
      //    the old title/path before the relay has propagated the new events.
      queryClient.setQueryData(
        ['nostrion-notes', user.pubkey],
        (old: any[] | undefined) => {
          if (!old) return old;
          const filtered = old.filter((e: any) => {
            if (e.kind === KIND_FILE) {
              const ed = e.tags?.find((t: string[]) => t[0] === 'd')?.[1];
              return ed !== dTag;
            }
            if (e.kind === KIND_VAULT_INDEX) return false;
            return true;
          });
          filtered.push(
            { kind: KIND_FILE, created_at: now, tags: [['d', dTag], ['encrypted', 'nip44']], content: fileEncrypted, id: `rename-file-${dTag}-${now}`, pubkey: user.pubkey, sig: '' },
            { kind: KIND_VAULT_INDEX, created_at: now + 1, tags: [['d', activeVaultDRef.current], ['encrypted', 'nip44']], content: indexEncrypted, id: `rename-index-${now}`, pubkey: user.pubkey, sig: '' }
          );
          return filtered;
        }
      );

      toast({ title: "Document renamed", description: `Renamed to "${newTitle}"` });
    } catch (err) {
      console.error('Failed to rename document:', err);
      // Revert optimistic update on failure
      setDecryptedNotes(currentNotes);
      if (selectedNoteD === dTag) setSelectedNote((prev: any) => prev ? { ...prev, payload: note.payload } : prev);
      toast({ title: "Error renaming document", description: "Please try again.", variant: "destructive" });
    }
  }, [user, publishEvent, queryClient, decryptedNotesRef, selectedNoteD, toast, KIND_FILE, KIND_VAULT_INDEX]);

  const handleRenameConfirm = useCallback(() => {
    if (renameDocD && renameTitleInput.trim()) {
      renameDocument(renameDocD, renameTitleInput.trim());
    }
    setShowRenameModal(false);
    setRenameDocD(null);
    setRenameTitleInput('');
  }, [renameDocD, renameTitleInput, renameDocument]);

  const openRenameModal = useCallback((dTag: string, currentTitle: string) => {
    setRenameDocD(dTag);
    setRenameTitleInput(currentTitle);
    setShowRenameModal(true);
  }, []);

  // Rebuild vault index from all existing file events (recovers lost/Onyx documents)
  const rebuildVaultIndex = useCallback(async () => {
    if (!user?.signer?.nip44 || !user.pubkey) {
      toast({
        title: "Not logged in",
        description: "Login to rebuild index",
        variant: "destructive",
      });
      return;
    }

    const now = Math.floor(Date.now() / 1000);
    const fileEvents = decryptedNotes.filter((n: any) => !n.isIndex && n.payload?.content);
    
    const files = fileEvents
      .map((n: any) => {
        const dTag = n.tags?.find((t: string[]) => t[0] === 'd')?.[1] || '';
        if (!dTag) return null;
        return {
          eventId: n.id || '',
          d: dTag,
          path: n.payload.path || `/notes/${dTag}.md`,
          title: String(n.payload?.title || getNoteTitle(n) || 'Untitled'),
          checksum: n.payload.checksum || '',
          version: n.payload.version || 1,
          modified: n.payload.modified || n.created_at || now,
        };
      })
      .filter((f: any) => f !== null)
      .sort((a: any, b: any) => (b.modified || 0) - (a.modified || 0));

    // Preserve existing folders[] and deleted[] — rebuild only reconstructs the files[] manifest
    const existingIndexForRebuild = decryptedNotes.find((n: any) => n.isIndex);
    const existingFoldersForRebuild: string[] = existingIndexForRebuild?.payload?.folders || [];
    // Note: rebuild intentionally clears deleted[] since all discovered events become active files.
    // This is the correct Onyx-compatible behaviour for a full rebuild from relay events.
    const indexPayload = {
      name: "Nostrion Workspace",
      description: "Private encrypted documents synced via Nostr. Rebuilt from all events.",
      files,
      folders: existingFoldersForRebuild,
      deleted: [], // explicit full rebuild — treats all relay events as active
      updated: now,
      rebuiltAt: now,
      totalDocuments: files.length,
    };

    try {
      const indexEncrypted = await user.signer.nip44.encrypt(user.pubkey, JSON.stringify(indexPayload));
      await publishEvent({
        kind: KIND_VAULT_INDEX,
        created_at: now,
        tags: [['d', activeVaultDRef.current], ['encrypted', 'nip44']],
        content: indexEncrypted,
      } as any);
      // Update cache directly — relay may still serve stale index for a few seconds
      if (user?.pubkey) {
        queryClient.setQueryData(
          ['nostrion-notes', user.pubkey],
          (old: any[] | undefined) => {
            if (!old) return old;
            const filtered = old.filter((e: any) => e.kind !== KIND_VAULT_INDEX);
            filtered.push({ kind: KIND_VAULT_INDEX, created_at: now, tags: [['d', activeVaultDRef.current], ['encrypted', 'nip44']], content: indexEncrypted, id: `rebuild-index-${now}`, pubkey: user.pubkey, sig: '' });
            return filtered;
          }
        );
      }
      toast({
        title: "Vault index rebuilt",
        description: `Successfully indexed ${files.length} documents from Nostr events.`,
      });
      console.log(`Rebuilt vault index with ${files.length} documents`);
    } catch (err) {
      console.error('Failed to rebuild vault index:', err);
      toast({
        title: "Rebuild failed",
        description: "Check console for details",
        variant: "destructive",
      });
    }
  }, [user, decryptedNotes, publishEvent, queryClient, toast, getNoteTitle, KIND_VAULT_INDEX]);

  // Publish database row as encrypted addressable event (kind 30078 per NIP-78)
  const publishDatabaseRow = useCallback(async (row: any) => {
    if (!user?.signer?.nip44 || !user.pubkey) {
      console.log('Not logged in or no signer - row saved locally only');
      return;
    }

    const now = Math.floor(Date.now() / 1000);
    const payload = {
      ...row,
      type: 'task',
      modified: now,
    };

    try {
      const encrypted = await user.signer.nip44.encrypt(
        user.pubkey, 
        JSON.stringify(payload)
      );

      const event = {
        kind: 30078,
        created_at: now,
        tags: [
          ['d', row.d || `db-row-${Date.now().toString(36)}`],
          ['t', 'task'],
          ['t', 'nostrion'],
          ['t', row.status?.toLowerCase().replace(' ', '-') || 'todo'],
          ['alt', 'Nostrion database row - encrypted with NIP-44'],
          ['encrypted', 'nip44'],
        ],
        content: encrypted,
      };

      await publishEvent(event as any);
      if (user?.pubkey) {
        queryClient.invalidateQueries({ queryKey: ['nostrion-notes', user.pubkey] });
      }
      console.log('Published database row to Nostr as kind 30078 d=' + (row.d || 'new'));
      
      toast({
        title: "Database updated",
        description: "Row synced privately to Nostr.",
      });
    } catch (err) {
      console.error('Failed to publish database row:', err);
      toast({
        title: "Sync error",
        description: "Failed to sync database row.",
        variant: "destructive",
      });
    }
  }, [user, publishEvent, queryClient, toast]);

  // Manual save function — called by Cmd/Ctrl+S and the save button
  // Only publishes the kind-30800 file event. The vault index (30801) is NOT touched
  // during content saves — it only needs updating for structural changes (create/rename/delete/move).
  // This prevents the vault index from overwriting deleted[] or folders[] with stale data.
  const saveCurrentNote = useCallback(async (content: string) => {
    if (!user?.signer?.nip44 || !user.pubkey) return;
    const d = selectedNoteD || MAIN_NOTE_D;
    const now = Math.floor(Date.now() / 1000);

    // Always read from the ref so we get the freshest in-memory state,
    // not a stale closure — this is critical after renames/moves.
    const currentNotes = decryptedNotesRef.current;
    const matchingNote = currentNotes.find((n: any) => {
      const nd = n.tags?.find((t: string[]) => t[0] === 'd')?.[1];
      return nd === d;
    });

    // Preserve the existing path exactly — never rewrite Onyx relative paths.
    let path: string = matchingNote?.payload?.path || '';
    if (!path) {
      const derivedTitle = deriveTitleFromPayload(matchingNote?.payload, d);
      const slug = derivedTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'untitled';
      path = `/notes/${slug}.md`;
    }

    // Only carry forward `title` if the note already has one.
    // Onyx notes have no title field — don't inject 'Untitled Document'.
    const existingTitle: string | undefined = matchingNote?.payload?.title && typeof matchingNote.payload.title === 'string'
      ? matchingNote.payload.title
      : undefined;

    const contentChecksum = await calculateChecksum(content);
    const payload: Record<string, unknown> = {
      ...(existingTitle ? { title: existingTitle } : {}),
      path,
      content,
      checksum: contentChecksum,
      version: (matchingNote?.payload?.version || 0) + 1,
      modified: now,
    };

    setSyncStatus('syncing');
    try {
      const encrypted = await user.signer.nip44.encrypt(user.pubkey, JSON.stringify(payload));
      const publishedSaveEvent = await publishEvent({
        kind: KIND_FILE,
        created_at: now,
        tags: [
          ['d', d],
          ['encrypted', 'nip44'],
        ],
        content: encrypted,
      } as any);
      const realSaveEventId: string = publishedSaveEvent?.id || `save-${d}-${now}`;

      // Update the TanStack cache directly so the decrypt effect sees the new
      // version and doesn't resurrect stale data from the relay on next refetch.
      if (user.pubkey) {
        queryClient.setQueryData(
          ['nostrion-notes', user.pubkey],
          (old: any[] | undefined) => {
            if (!old) return old;
            const filtered = old.filter((e: any) => {
              if (e.kind !== KIND_FILE) return true;
              const ed = e.tags?.find((t: string[]) => t[0] === 'd')?.[1];
              return ed !== d;
            });
            filtered.push({
              kind: KIND_FILE,
              created_at: now,
              tags: [['d', d], ['encrypted', 'nip44']],
              content: encrypted,
              id: realSaveEventId,
              pubkey: user.pubkey,
              sig: '',
            });
            return filtered;
          }
        );
      }

      // Also update decryptedNotes in-memory so selectedNote stays current.
      // Update the vault index entry for this file with the new eventId so Onyx
      // can find the latest version of this file by event ID.
      setDecryptedNotes(prev => prev.map((n: any) => {
        const nd = n.tags?.find((t: string[]) => t[0] === 'd')?.[1];
        if (nd === d) return { ...n, id: realSaveEventId, payload };
        // Update vault index file entry eventId to point to the latest file event
        if (n.isIndex && n.payload?.files) {
          const updFiles = (n.payload.files as any[]).map((f: any) => {
            if (f.d !== d) return f;
            return { ...f, eventId: realSaveEventId, modified: now, version: payload.version };
          });
          return { ...n, payload: { ...n.payload, files: updFiles } };
        }
        return n;
      }));
      if (selectedNote) {
        setSelectedNote((prev: any) => prev ? { ...prev, id: realSaveEventId, payload } : prev);
      }

      setSyncStatus('synced');
      console.log('Saved note to Nostr (kind 30800, d=' + d + ', id=' + realSaveEventId + ')');
    } catch (err) {
      console.error('Save failed:', err);
      setSyncStatus('pending');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, selectedNoteD, MAIN_NOTE_D, decryptedNotesRef, publishEvent, queryClient, KIND_FILE, selectedNote]);

  // Cmd/Ctrl+S manual save — keyboard shortcut.
  // MUST be defined after saveCurrentNote to avoid temporal dead zone errors.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        if (selectedNoteD && noteContent) {
          saveCurrentNote(noteContent);
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedNoteD, noteContent, saveCurrentNote]);

  // Auto-save debounce — fires 3 seconds after the user stops typing.
  // ONLY publishes the kind-30800 file event. The vault index (30801) is intentionally
  // NOT updated here — that's only done during structural ops (create/rename/delete/move)
  // so that deleted[] and folders[] are never silently overwritten by a content save.
  useEffect(() => {
    if (!autoSyncEnabled) return;
    if (syncStatus !== 'pending') return;
    if (!noteContent.trim()) return;
    if (currentView !== 'notes') return;
    if (!selectedNoteD) return;

    const timer = setTimeout(async () => {
      await saveCurrentNote(noteContent);
    }, 3000); // 3-second debounce — long enough to avoid mid-word saves with NIP-07 signers
    return () => clearTimeout(timer);
  }, [noteContent, currentView, selectedNoteD, autoSyncEnabled, syncStatus, saveCurrentNote]);

  const handleWipeAllData = async () => {
    try {
      // Clear all DM cache (IndexedDB)
      await clearAllMessages().catch(console.warn);

      // Clear sensitive localStorage
      const keysToClear = [
        'nostr:login',
        'nostr:app-config',
        'nwc-connections',
        'nwc-active-connection',
        'nostrion-auto-sync',
        'nostrion-clear-cache-on-logout',
        'nostrion-auto-clear-inactivity',
      ];
      keysToClear.forEach(key => localStorage.removeItem(key));

      // Clear TanStack Query cache so no decrypted data remains in memory
      queryClient.clear();

      // Logout
      logout();

      setShowSettings(false);
      setShowWipeConfirm(false);

      console.log('✅ All local data wiped and logged out for privacy');

      // Reload to ensure clean state
      setTimeout(() => window.location.reload(), 500);
    } catch (error) {
      console.error('Wipe failed:', error);
      alert('Partial wipe completed. Please refresh the page.');
    }
  };

  return (
    <div className="h-screen bg-background text-foreground flex flex-col overflow-hidden">
      {/* Top Navigation */}
      <header className="border-b h-14 flex items-center px-3 sm:px-6 justify-between bg-card z-50 gap-2">
        <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
          {user && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => {
                if (isMobile) {
                  setMobileSidebarOpen(!mobileSidebarOpen);
                } else {
                  setSidebarCollapsed(!sidebarCollapsed);
                }
              }}
            >
              <PanelLeft className={`w-5 h-5 transition-transform ${!isMobile && sidebarCollapsed ? 'rotate-180' : ''}`} />
            </Button>
          )}
           <div className="flex items-center">
             <img 
               src="/nostrion_typography_black.png" 
               className="h-6 sm:h-7 dark:hidden" 
               alt="Nostrion" 
             />
             <img 
               src="/nostrion_typography_white.png" 
               className="h-6 sm:h-7 hidden dark:block" 
               alt="Nostrion" 
             />
           </div>
        </div>
        
        <div className="flex items-center gap-2 sm:gap-4 flex-1 justify-end">
          {/* Desktop: full-width search bar */}
          <div className="relative w-80 hidden sm:block">
            <input 
              type="text" 
              placeholder="Quick find or jump to..." 
              className="w-full bg-muted/50 border border-border rounded-lg pl-10 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
            />
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 absolute left-3.5 top-3 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 01-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          {/* Mobile: expandable search */}
          {mobileSearchOpen ? (
            <div className="flex items-center flex-1 sm:hidden">
              <div className="relative flex-1">
                <input 
                  type="text"
                  autoFocus
                  placeholder="Quick find..."
                  className="w-full bg-muted/50 border border-border rounded-lg pl-9 pr-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                />
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-2 text-muted-foreground" />
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 ml-1 flex-shrink-0"
                onClick={() => setMobileSearchOpen(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 sm:hidden flex-shrink-0"
              onClick={() => setMobileSearchOpen(true)}
            >
              <Search className="w-4 h-4" />
            </Button>
          )}
          
          <LoginArea className="flex flex-shrink-0" />
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden relative">
        {/* Mobile sidebar backdrop */}
        {user && isMobile && mobileSidebarOpen && (
          <div
            className="absolute inset-0 bg-black/40 z-30"
            onClick={() => setMobileSidebarOpen(false)}
          />
        )}

        {user && (
          /* Sidebar - only shown when logged in */
          /* On mobile: overlay drawer sliding in from left. On desktop: inline collapsible. */
          <div className={`
            bg-card flex flex-col h-full flex-shrink-0 overflow-hidden transition-all duration-300
            ${isMobile
              ? `absolute left-0 top-0 bottom-0 z-40 border-r shadow-xl ${mobileSidebarOpen ? 'w-80 translate-x-0' : 'w-80 -translate-x-full'}`
              : `border-r relative ${sidebarCollapsed ? 'w-16' : 'w-72'}`
            }
          `}>
             <div className="p-4 flex flex-col flex-1 min-h-0">
               <div className="space-y-1 flex-shrink-0 pt-2">

                 {sidebarItems.map((item) => {
                     const isComingSoon = item.id === 'database' || item.id === 'board';
                     // On mobile the drawer is full-width, always show labels; on desktop respect sidebarCollapsed
                     const showLabel = isMobile || !sidebarCollapsed;
                     return (
                       <div 
                         key={item.id}
                         onClick={() => {
                           if (!isComingSoon) {
                             setCurrentView(item.id);
                             if (isMobile) setMobileSidebarOpen(false);
                           }
                         }}
                         className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                           isComingSoon
                             ? 'opacity-40 cursor-not-allowed'
                             : `hover:bg-accent cursor-pointer ${currentView === item.id ? 'bg-accent font-medium' : ''}`
                         } ${!showLabel ? 'justify-center' : ''}`}
                       >
                         <item.icon className={`flex-shrink-0 ${!showLabel ? 'w-5 h-5' : 'w-4 h-4'}`} />
                         {showLabel && (
                           <span className="flex items-center gap-2 flex-1">
                             {item.label}
                             {isComingSoon && (
                               <span className="text-[9px] font-medium uppercase tracking-wide opacity-70 bg-muted px-1.5 py-0.5 rounded">
                                 soon
                               </span>
                             )}
                           </span>
                         )}
                       </div>
                     );
                   })}
               </div>

                   {/* Dynamic Notes List - Onyx-style folder/file tree with DnD */}
                    {currentView === 'notes' && (isMobile || !sidebarCollapsed) && (
                     <div className="mt-6 px-2 flex-1 flex flex-col min-h-0 overflow-hidden">
                       {/* Header row: YOUR DOCUMENTS + new folder + new document icons */}
                       <div className="flex items-center justify-between mb-1.5 px-3">
                         <span className="text-xs font-medium text-muted-foreground tracking-wide">YOUR DOCUMENTS</span>
                         <div className="flex items-center gap-0.5">
                           <Button
                             variant="ghost"
                             size="icon"
                             className="h-6 w-6 text-muted-foreground hover:text-foreground"
                             title="New Folder"
                             onClick={() => {
                               setNewFolderParentPath('/');
                               setNewFolderNameInput('');
                               setShowNewFolderModal(true);
                             }}
                           >
                             <FolderPlus className="w-3.5 h-3.5" />
                           </Button>
                           <Button
                             variant="ghost"
                             size="icon"
                             className="h-6 w-6 text-muted-foreground hover:text-foreground"
                             title="New Document"
                             onClick={() => {
                               setCurrentView('notes');
                               setNewDocTitleInput('');
                               setShowNewDocModal(true);
                             }}
                           >
                             <FilePlus className="w-3.5 h-3.5" />
                           </Button>
                         </div>
                       </div>

                       {documentList.length === 0 ? (
                         <div className="px-3 py-4 text-xs text-muted-foreground text-center">
                           No documents yet.<br />
                           <button
                             className="underline mt-1 hover:text-foreground"
                             onClick={() => { setNewDocTitleInput(''); setShowNewDocModal(true); }}
                           >
                             Create your first document
                           </button>
                         </div>
                       ) : (
                          <DndContext
                            sensors={sensors}
                            collisionDetection={pointerWithin}
                            onDragStart={handleDragStart}
                            onDragOver={handleDragOver}
                            onDragEnd={handleDragEnd}
                          >
                             <SortableContext items={flatTreeIds} strategy={verticalListSortingStrategy}>
                               <div className="space-y-0.5 flex-1 overflow-auto pr-1 custom-scroll">
                                 {(() => {
                                   const isDraggingFile = activeId?.startsWith('file:') ?? false;

                                  // Recursive tree node renderer
                                  const renderNode = (node: any, depth: number): React.ReactNode => {
                                    if (node.isFolder) {
                                      const isExpanded = expandedPaths.includes(node.path);
                                      const childEntries = Object.values(node.children || {}) as any[];
                                      const folderId = `folder:${node.path}`;
                                      return (
                                        // DroppableFolderNode makes the entire folder row + children a drop target
                                        <DroppableFolderNode key={node.path} id={folderId} isDragActive={isDraggingFile}>
                                          <ContextMenu>
                                            <ContextMenuTrigger asChild>
                                              <div
                                                onClick={() => toggleFolder(node.path)}
                                                style={{ paddingLeft: `${12 + depth * 16}px` }}
                                                className="group flex items-center gap-2 px-3 py-2 text-xs rounded-xl cursor-pointer transition-colors text-muted-foreground hover:text-foreground hover:bg-accent select-none"
                                              >
                                                {isExpanded
                                                  ? <ChevronDown className="w-3 h-3 flex-shrink-0 opacity-60" />
                                                  : <ChevronRight className="w-3 h-3 flex-shrink-0 opacity-60" />}
                                                {isExpanded
                                                  ? <FolderOpen className="flex-shrink-0 w-4 h-4 text-amber-500" />
                                                  : <Folder className="flex-shrink-0 w-4 h-4 text-amber-500" />}
                                                <span className="truncate font-medium">{node.name}</span>
                                              </div>
                                            </ContextMenuTrigger>
                                             <ContextMenuContent>
                                              <ContextMenuItem
                                                onClick={() => {
                                                  setRenameFolderPath(node.path);
                                                  setRenameFolderInput(node.name);
                                                  setShowRenameFolderModal(true);
                                                }}
                                              >
                                                <Edit3 className="w-4 h-4 mr-2" /> Rename
                                              </ContextMenuItem>
                                              <ContextMenuSeparator />
                                              <ContextMenuItem
                                                onClick={() => {
                                                  setNewFolderParentPath(node.path);
                                                  setNewFolderNameInput('');
                                                  setShowNewFolderModal(true);
                                                }}
                                              >
                                                <FolderPlus className="w-4 h-4 mr-2" /> New Subfolder
                                              </ContextMenuItem>
                                              <ContextMenuItem
                                                onClick={() => {
                                                  setNewDocTitleInput('');
                                                  setShowNewDocModal(true);
                                                }}
                                              >
                                                <FilePlus className="w-4 h-4 mr-2" /> New Document Here
                                              </ContextMenuItem>
                                              <ContextMenuSeparator />
                                              <ContextMenuItem
                                                className="text-destructive focus:text-destructive"
                                                onClick={() => {
                                                  setDeleteTarget({ folderPath: node.path, name: node.name, isFolder: true });
                                                  setShowDeleteConfirm(true);
                                                }}
                                              >
                                                <Trash2 className="w-4 h-4 mr-2" /> Delete Folder
                                              </ContextMenuItem>
                                            </ContextMenuContent>
                                          </ContextMenu>
                                          {/* Children — only render when expanded */}
                                          {isExpanded && childEntries.length > 0 && (
                                            <div>
                                              {childEntries
                                                .sort((a, b) => {
                                                  if (a.isFolder && !b.isFolder) return -1;
                                                  if (!a.isFolder && b.isFolder) return 1;
                                                  return a.name.localeCompare(b.name);
                                                })
                                                .map((child) => renderNode(child, depth + 1))}
                                            </div>
                                          )}
                                        </DroppableFolderNode>
                                      );
                                    } else {
                                      // File leaf node — sortable (draggable)
                                      const dTag = node.dTag || '';
                                      const displayTitle = node.name || dTag;
                                      const fileId = `file:${dTag}`;
                                      const isActive = selectedNoteD === dTag;
                                      return (
                                        <SortableFileNode key={node.path || dTag} id={fileId}>
                                          <ContextMenu>
                                            <ContextMenuTrigger asChild>
                                              <div
                                                 onClick={() => {
                                                   const fullNote = decryptedNotes.find((n: any) => {
                                                     const nd = n.tags?.find((t: string[]) => t[0] === 'd')?.[1];
                                                     return nd === dTag;
                                                   }) || node.note;
                                                    if (!fullNote) return;
                                                   setSelectedNote(fullNote);
                                                   const content = fullNote.payload?.content || fullNote.content || '';
                                                   const safeContent = typeof content === 'string' ? content : '';
                                                   setNoteContent(safeContent);
                                                   setNoteIsCorrupted(looksLikeCiphertext(safeContent));
                                                   setSelectedNoteD(dTag);
                                                   // Close mobile sidebar after selecting a document
                                                   if (isMobile) setMobileSidebarOpen(false);
                                                 }}
                                                style={{ paddingLeft: `${12 + depth * 16}px` }}
                                                className={`group flex items-center justify-between gap-2 px-3 py-2 text-xs rounded-xl cursor-pointer transition-colors text-muted-foreground hover:text-foreground ${
                                                  isActive ? 'bg-accent text-foreground' : 'hover:bg-accent'
                                                }`}
                                              >
                                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                                  <DragHandle />
                                                  <File className="flex-shrink-0 w-4 h-4" />
                                                  <span className="truncate">{displayTitle}</span>
                                                </div>
                                                <Button
                                                  variant="ghost"
                                                  size="icon"
                                                  className="h-6 w-6 opacity-0 group-hover:opacity-100 hover:bg-accent-foreground/10 text-muted-foreground hover:text-foreground flex-shrink-0"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    openRenameModal(dTag, displayTitle);
                                                  }}
                                                >
                                                  <Edit3 className="h-3 w-3" />
                                                </Button>
                                              </div>
                                            </ContextMenuTrigger>
                                            <ContextMenuContent>
                                              <ContextMenuItem onClick={() => openRenameModal(dTag, displayTitle)}>
                                                <Edit3 className="w-4 h-4 mr-2" /> Rename
                                              </ContextMenuItem>
                                              <ContextMenuSeparator />
                                              <ContextMenuItem
                                                className="text-destructive focus:text-destructive"
                                                onClick={() => {
                                                  setDeleteTarget({ dTag, name: displayTitle, isFolder: false });
                                                  setShowDeleteConfirm(true);
                                                }}
                                              >
                                                <Trash2 className="w-4 h-4 mr-2" /> Delete
                                              </ContextMenuItem>
                                            </ContextMenuContent>
                                          </ContextMenu>
                                        </SortableFileNode>
                                      );
                                    }
                                  };

                                  // Get top-level children of the root tree node
                                  const topLevelEntries = Object.values(fileTree.children || {}) as any[];
                                  return topLevelEntries
                                    .sort((a, b) => {
                                      if (a.isFolder && !b.isFolder) return -1;
                                      if (!a.isFolder && b.isFolder) return 1;
                                      return a.name.localeCompare(b.name);
                                    })
                                    .map((node) => renderNode(node, 0));
                                 })()}
                               </div>
                               {/* Root drop zone strip — only visible while dragging a file */}
                               <RootDropZone isDragActive={activeId?.startsWith('file:') ?? false} />
                             </SortableContext>
                           <DragOverlay>
                             {activeId ? (
                               <div className="bg-card border rounded-xl px-3 py-2 text-xs shadow-lg flex items-center gap-2 opacity-90">
                                 {activeId.startsWith('folder:') ? (
                                   <Folder className="w-4 h-4 text-amber-500" />
                                 ) : (
                                   <File className="w-4 h-4" />
                                 )}
                                 <span className="truncate max-w-[140px]">
                                   {activeId.startsWith('folder:')
                                     ? activeId.replace('folder:/', '').split('/').pop()
                                     : (() => {
                                         const dTag = activeId.replace('file:', '');
                                         const note = documentList.find((n: any) => n.tags?.[0]?.[1] === dTag);
                                         return note?.payload?.title || dTag;
                                       })()
                                   }
                                 </span>
                               </div>
                             ) : null}
                           </DragOverlay>
                         </DndContext>
                       )}
                     </div>
                   )}

                 {/* Bottom section - always visible at bottom */}
                 <div className="mt-auto pt-4 border-t flex-shrink-0">
                   {(isMobile || !sidebarCollapsed) ? (
                    <div className="p-2">
                      {/* Sync status row */}
                      <div className="px-2 mb-1 flex items-center gap-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex items-center gap-1.5 ${getSyncPill.className}`}>
                          <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${getSyncPill.dotClass} ${getSyncPill.animate ? 'animate-pulse' : ''}`}></div>
                          {getSyncPill.text}
                        </span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 flex-shrink-0 text-muted-foreground hover:text-foreground ml-auto"
                          title="Sync"
                          onClick={async () => {
                            setSyncStatus('syncing');
                            await refetchNotes();
                            setSyncStatus('synced');
                            toast({ title: "Synced", description: "Latest notes and data loaded from Nostr." });
                          }}
                        >
                          <RefreshCw className="w-3 h-3" />
                        </Button>
                      </div>
                      {/* Pubkey / stats row */}
                      <div className="px-2 mb-3">
                        <span className="text-xs text-muted-foreground/60">
                          {user?.pubkey.slice(0, 8)}… · {(rawNotes?.length || 0)} events · {decryptedNotes.filter((n: any) => !n.isIndex).length} docs
                        </span>
                      </div>

                      <div className="text-xs text-muted-foreground mb-3 px-2 flex items-center justify-between">
                        <span>PRIVATE WORKSPACE</span>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-7 w-7 -mr-1 text-muted-foreground hover:text-foreground"
                          onClick={() => setShowSettings(true)}
                        >
                          <Settings className="h-4 w-4" />
                        </Button>
                      </div>
                      
                      <a 
                        href="https://shakespeare.diy" 
                        target="_blank"
                        className="text-xs flex items-center gap-1.5 text-muted-foreground hover:text-foreground px-2"
                      >
                        Vibed with Shakespeare
                      </a>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2 py-2">
                      {/* Sync status dot */}
                      <div
                        className={`w-2 h-2 rounded-full ${getSyncPill.dotClass} ${getSyncPill.animate ? 'animate-pulse' : ''}`}
                        title={getSyncPill.text}
                      />
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-9 w-9 text-muted-foreground hover:text-foreground"
                        onClick={() => setShowSettings(true)}
                      >
                        <Settings className="h-5 w-5" />
                      </Button>
                    </div>
                  )}
                </div>
               </div> {/* end of flex-1 p-4 container */}

          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden bg-zinc-50 dark:bg-zinc-950">


          {/* View Content */}
           <div className={`flex-1 overflow-auto ${currentView === 'notes' ? 'p-0' : 'p-4 sm:p-8'}`}>
             {!user && (() => {
               const colA = [
                 "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.",
                 "At vero eos et accusamus et iusto odio dignissimos ducimus qui blanditiis praesentium voluptatum deleniti atque corrupti quos dolores et quas molestias excepturi sint occaecati cupiditate non provident, similique sunt in culpa qui officia deserunt mollitia animi, id est laborum et dolorum fuga.",
                 "Praesent commodo cursus magna, vel scelerisque nisl consectetur et. Curabitur blandit tempus porttitor. Nullam quis risus eget urna mollis ornare vel eu leo. Donec ullamcorper nulla non metus auctor fringilla. Maecenas sed diam eget risus varius blandit sit amet non magna.",
                 "Fusce dapibus, tellus ac cursus commodo, tortor mauris condimentum nibh, ut fermentum massa justo sit amet risus. Etiam porta sem malesuada magna mollis euismod. Donec sed odio dui. Cras mattis consectetur purus sit amet fermentum. Morbi leo risus, porta ac consectetur ac, vestibulum at eros.",
                 "Donec id elit non mi porta gravida at eget metus. Maecenas faucibus mollis interdum. Cras justo odio, dapibus ac facilisis in, egestas eget quam. Nullam id dolor id nibh ultricies vehicula ut id elit. Cum sociis natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus.",
                 "Quis autem vel eum iure reprehenderit qui in ea voluptate velit esse quam nihil molestiae consequatur, vel illum qui dolorem eum fugiat quo voluptas nulla pariatur? Neque porro quisquam est, qui dolorem ipsum quia dolor sit amet, consectetur, adipisci velit, sed quia non numquam eius modi tempora incidunt.",
                 "Phasellus ornare nulla sit amet nulla tincidunt aliquet. Curabitur fringilla purus at diam semper, vel porttitor lorem malesuada. Aliquam erat volutpat. Proin at lectus id ante gravida varius vel eu orci. Pellentesque ac purus vitae orci sodales iaculis non sed urna.",
               ];
               const colB = [
                 "Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt explicabo. Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit.",
                 "Nam libero tempore, cum soluta nobis est eligendi optio cumque nihil impedit quo minus id quod maxime placeat facere possimus, omnis voluptas assumenda est, omnis dolor repellendus. Temporibus autem quibusdam et aut officiis debitis aut rerum necessitatibus saepe eveniet.",
                 "Itaque earum rerum hic tenetur a sapiente delectus, ut aut reiciendis voluptatibus maiores alias consequatur aut perferendis doloribus asperiores repellat. Nulla facilisi. Cras ultricies ligula sed magna dictum porta. Vivamus magna. Fusce dui leo, imperdiet in, aliquam sit amet, feugiat a, tellus.",
                 "Vestibulum ante ipsum primis in faucibus orci luctus et ultrices posuere cubilia curae; Pellentesque habitant morbi tristique senectus et netus et malesuada fames ac turpis egestas. Aenean lacinia bibendum nulla sed consectetur. Integer posuere erat a ante venenatis dapibus posuere velit aliquet.",
                 "Integer nec odio. Praesent libero. Sed cursus ante dapibus diam. Sed nisi. Nulla quis sem at nibh elementum imperdiet. Duis sagittis ipsum. Praesent mauris. Fusce nec tellus sed augue semper porta. Mauris massa. Vestibulum lacinia arcu eget nulla. Class aptent taciti sociosqu ad litora torquent.",
                 "Curabitur pretium tincidunt lacus. Nulla gravida orci a odio. Nullam varius, turpis molestie dictum semper, turpis ante interdum risus, eget vulputate leo lorem et lectus. Quisque volutpat condimentum velit. Class aptent taciti sociosqu ad litora torquent per conubia nostra.",
                 "Aliquam erat volutpat. Nunc eleifend leo vitae magna. In id erat non orci commodo lobortis. Proin neque massa, cursus ut, gravida ut, lobortis eget, lacus. Sed diam. Praesent fermentum tempor tellus. Nullam tempus. Mauris ac felis vel velit tristique imperdiet.",
               ];
               // 4 copies — animate -25% so exactly one copy scrolls off, 3 remain as buffer
               const quadA = [...colA, ...colA, ...colA, ...colA];
               const quadB = [...colB, ...colB, ...colB, ...colB];
               return (
                 <div className="h-full relative overflow-hidden flex items-center justify-center isolate">
                   {/* Two independently scrolling columns */}
                   <div
                     aria-hidden="true"
                     className="absolute inset-0 select-none pointer-events-none flex gap-10 px-14"
                     style={{ opacity: 0.045 }}
                   >
                     <div className="flex-1 overflow-hidden">
                       <div className="lorem-scroll-up text-[13px] leading-[1.9] text-foreground font-serif space-y-5">
                         {quadA.map((text, i) => <p key={i}>{text}</p>)}
                       </div>
                     </div>
                     <div className="flex-1 overflow-hidden">
                       <div className="lorem-scroll-down text-[13px] leading-[1.9] text-foreground font-serif space-y-5">
                         {quadB.map((text, i) => <p key={i}>{text}</p>)}
                       </div>
                     </div>
                   </div>

                   {/* Foreground content */}
                   <div className="relative z-10 max-w-md text-center space-y-8">
                     <div className="mx-auto mb-6 flex flex-col items-center">
                       <img
                         src="/nostrion_icon_black.png"
                         className="w-32 h-32 dark:hidden"
                         alt="Nostrion icon"
                       />
                       <img
                         src="/nostrion_icon_white.png"
                         className="w-32 h-32 hidden dark:block"
                         alt="Nostrion icon"
                       />
                       <img
                         src="/nostrion_typography_black.png"
                         className="mt-4 h-20 dark:hidden"
                         alt="Nostrion"
                       />
                       <img
                         src="/nostrion_typography_white.png"
                         className="mt-4 h-20 hidden dark:block"
                         alt="Nostrion"
                       />
                     </div>
                     <p className="text-xl text-muted-foreground">Private Notes &amp; Databases</p>
                     <p className="text-muted-foreground max-w-xs mx-auto">
                       Encrypted productivity workspace on the Nostr protocol.
                       Your data is yours — synced privately across devices.
                     </p>
                     <div className="flex items-center justify-center gap-8 text-sm pt-8 border-t">
                       <div className="flex items-center gap-2">
                         <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                         <span className="text-muted-foreground">End-to-end encrypted</span>
                       </div>
                       <div className="text-muted-foreground">•</div>
                       <div className="text-muted-foreground">Decentralized</div>
                     </div>
                     <a
                       href="https://shakespeare.diy"
                       target="_blank"
                       className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mt-8"
                     >
                       Vibed with Shakespeare
                     </a>
                   </div>
                 </div>
               );
             })()}
            {user && currentView === 'notes' && (
              <div className="h-full flex flex-col">

                {/* Empty state — no note selected */}
                {!selectedNoteD && (() => {
                  const colA = [
                    "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.",
                    "At vero eos et accusamus et iusto odio dignissimos ducimus qui blanditiis praesentium voluptatum deleniti atque corrupti quos dolores et quas molestias excepturi sint occaecati cupiditate non provident, similique sunt in culpa qui officia deserunt mollitia animi, id est laborum et dolorum fuga.",
                    "Praesent commodo cursus magna, vel scelerisque nisl consectetur et. Curabitur blandit tempus porttitor. Nullam quis risus eget urna mollis ornare vel eu leo. Donec ullamcorper nulla non metus auctor fringilla. Maecenas sed diam eget risus varius blandit sit amet non magna.",
                    "Fusce dapibus, tellus ac cursus commodo, tortor mauris condimentum nibh, ut fermentum massa justo sit amet risus. Etiam porta sem malesuada magna mollis euismod. Donec sed odio dui. Cras mattis consectetur purus sit amet fermentum. Morbi leo risus, porta ac consectetur ac, vestibulum at eros.",
                    "Donec id elit non mi porta gravida at eget metus. Maecenas faucibus mollis interdum. Cras justo odio, dapibus ac facilisis in, egestas eget quam. Nullam id dolor id nibh ultricies vehicula ut id elit. Cum sociis natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus.",
                    "Quis autem vel eum iure reprehenderit qui in ea voluptate velit esse quam nihil molestiae consequatur, vel illum qui dolorem eum fugiat quo voluptas nulla pariatur? Neque porro quisquam est, qui dolorem ipsum quia dolor sit amet, consectetur, adipisci velit, sed quia non numquam eius modi tempora incidunt.",
                    "Phasellus ornare nulla sit amet nulla tincidunt aliquet. Curabitur fringilla purus at diam semper, vel porttitor lorem malesuada. Aliquam erat volutpat. Proin at lectus id ante gravida varius vel eu orci. Pellentesque ac purus vitae orci sodales iaculis non sed urna.",
                  ];
                  const colB = [
                    "Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt explicabo. Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit.",
                    "Nam libero tempore, cum soluta nobis est eligendi optio cumque nihil impedit quo minus id quod maxime placeat facere possimus, omnis voluptas assumenda est, omnis dolor repellendus. Temporibus autem quibusdam et aut officiis debitis aut rerum necessitatibus saepe eveniet.",
                    "Itaque earum rerum hic tenetur a sapiente delectus, ut aut reiciendis voluptatibus maiores alias consequatur aut perferendis doloribus asperiores repellat. Nulla facilisi. Cras ultricies ligula sed magna dictum porta. Vivamus magna. Fusce dui leo, imperdiet in, aliquam sit amet, feugiat a, tellus.",
                    "Vestibulum ante ipsum primis in faucibus orci luctus et ultrices posuere cubilia curae; Pellentesque habitant morbi tristique senectus et netus et malesuada fames ac turpis egestas. Aenean lacinia bibendum nulla sed consectetur. Integer posuere erat a ante venenatis dapibus posuere velit aliquet.",
                    "Integer nec odio. Praesent libero. Sed cursus ante dapibus diam. Sed nisi. Nulla quis sem at nibh elementum imperdiet. Duis sagittis ipsum. Praesent mauris. Fusce nec tellus sed augue semper porta. Mauris massa. Vestibulum lacinia arcu eget nulla. Class aptent taciti sociosqu ad litora torquent.",
                    "Curabitur pretium tincidunt lacus. Nulla gravida orci a odio. Nullam varius, turpis molestie dictum semper, turpis ante interdum risus, eget vulputate leo lorem et lectus. Quisque volutpat condimentum velit. Class aptent taciti sociosqu ad litora torquent per conubia nostra.",
                    "Aliquam erat volutpat. Nunc eleifend leo vitae magna. In id erat non orci commodo lobortis. Proin neque massa, cursus ut, gravida ut, lobortis eget, lacus. Sed diam. Praesent fermentum tempor tellus. Nullam tempus. Mauris ac felis vel velit tristique imperdiet.",
                  ];
                   // 4 copies — animate -25% so exactly one copy scrolls off, 3 remain as buffer
                   const quadA = [...colA, ...colA, ...colA, ...colA];
                   const quadB = [...colB, ...colB, ...colB, ...colB];
                   return (
                     <div className="flex-1 relative overflow-hidden flex items-center justify-center bg-white dark:bg-zinc-950 isolate">
                       {/* Two independently scrolling columns */}
                       <div
                         aria-hidden="true"
                         className="absolute inset-0 select-none pointer-events-none flex gap-10 px-14"
                         style={{ opacity: 0.045 }}
                       >
                          {/* Left column — scrolls up */}
                          <div className="flex-1 overflow-hidden">
                            <div className="lorem-scroll-up text-[13px] leading-[1.9] text-foreground font-serif space-y-5">
                              {quadA.map((text, i) => (
                                <p key={i}>{text}</p>
                              ))}
                            </div>
                          </div>
                          {/* Right column — scrolls down */}
                          <div className="flex-1 overflow-hidden">
                            <div className="lorem-scroll-down text-[13px] leading-[1.9] text-foreground font-serif space-y-5">
                              {quadB.map((text, i) => (
                                <p key={i}>{text}</p>
                              ))}
                            </div>
                          </div>
                       </div>

                      {/* Centred icon + CTA */}
                      <div className="relative z-10 flex flex-col items-center text-center gap-6 max-w-sm">
                        <img
                          src="/nostrion_icon_black.png"
                          className="w-16 h-16 dark:hidden opacity-70"
                          alt="Nostrion"
                        />
                        <img
                          src="/nostrion_icon_white.png"
                          className="w-16 h-16 hidden dark:block opacity-70"
                          alt="Nostrion"
                        />
                        <p className="text-sm text-muted-foreground">
                          Select a document to open it, or create a new one.
                        </p>
                        <Button
                          onClick={() => {
                            setNewDocTitleInput('');
                            setShowNewDocModal(true);
                          }}
                          className="gap-2"
                        >
                          <FilePlus className="w-4 h-4" />
                          New Note
                        </Button>
                      </div>
                    </div>
                  );
                })()}

                {/* Note editor — only shown when a note is open */}
                {selectedNoteD && (
                  <>
                  {/* Minimal note title bar */}
                  <div className="bg-card border-b px-4 sm:px-8 py-3 sm:py-4 flex-shrink-0 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className="font-semibold text-lg sm:text-2xl truncate">{currentNoteTitle}</span>
                    </div>

                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      {/* Icon-only toggle — minimal, matches folder/doc icon style */}
                      {([
                        { value: 'edit' as const, icon: Edit3, label: 'Editor mode' },
                        // Hide split mode on mobile — too narrow to be useful
                        ...(!isMobile ? [{ value: 'split' as const, icon: Columns3, label: 'Split view' }] : []),
                        { value: 'preview' as const, icon: Eye, label: 'Read mode' },
                      ] as const).map(({ value, icon: Icon, label }) => (
                        <Button
                          key={value}
                          variant="ghost"
                          size="icon"
                          className={`h-7 w-7 transition-colors ${
                            notesMode === value
                              ? 'text-foreground bg-accent'
                              : 'text-muted-foreground hover:text-foreground'
                          }`}
                          aria-label={label}
                          onClick={() => setNotesMode(value)}
                        >
                          <Icon className="w-3.5 h-3.5" />
                        </Button>
                      ))}

                      {/* Separator + manual Save button + Close button */}
                      <div className="w-px h-4 bg-border mx-1" />
                      <Button
                        variant="ghost"
                        size="icon"
                        className={`h-7 w-7 transition-colors ${
                          syncStatus === 'pending'
                            ? 'text-amber-500 hover:text-amber-600'
                            : syncStatus === 'syncing'
                            ? 'text-blue-500 animate-pulse'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                        aria-label="Save note (⌘S)"
                        title={syncStatus === 'pending' ? 'Unsaved changes — click to save (⌘S)' : syncStatus === 'syncing' ? 'Saving…' : 'Saved (⌘S)'}
                        onClick={() => { if (noteContent) saveCurrentNote(noteContent); }}
                        disabled={syncStatus === 'syncing'}
                      >
                        <RefreshCw className={`w-3.5 h-3.5 ${syncStatus === 'syncing' ? 'animate-spin' : ''}`} />
                      </Button>
                      <div className="w-px h-4 bg-border mx-1" />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-foreground"
                        aria-label="Close note"
                        title="Close note"
                        onClick={() => {
                          setSelectedNote(null);
                          setSelectedNoteD(null);
                          setNoteContent('');
                          setNoteIsCorrupted(false);
                        }}
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>

                {/* Full-bleed content area touching left nav and top header */}
                <div className="flex-1 flex flex-col min-h-0 bg-white dark:bg-zinc-950 overflow-hidden">
                  {/* Corruption warning — shown when content is a ciphertext blob (double-encryption artifact) */}
                  {noteIsCorrupted && (
                    <div className="flex items-start gap-3 px-6 py-3 bg-amber-50 dark:bg-amber-950/40 border-b border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300 text-sm flex-shrink-0">
                      <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-medium">Note content could not be read.</span>
                        {' '}This note was saved with an incompatible encryption format (likely from an older version).
                        The data is encrypted and safe, but can't be displayed here.{' '}
                        <button
                          className="underline font-medium hover:text-amber-900 dark:hover:text-amber-200"
                          onClick={() => {
                            if (selectedNoteD) deleteFile(selectedNoteD);
                          }}
                        >
                          Delete this note
                        </button>
                        {' '}to remove it from your workspace.
                      </div>
                    </div>
                  )}
                   {/* Main content switches based on mode - split, edit, preview */}
                   {notesMode === 'split' ? (
                     <PanelGroup direction="horizontal" className="flex-1 h-full flex min-h-0 overflow-hidden">
                       {/* Editor Panel */}
                       <Panel defaultSize={50} minSize={25} className="flex flex-col">
                         <div className="h-10 border-b bg-muted/50 flex items-center px-6 text-xs font-medium text-muted-foreground">
                           <Edit3 className="w-3.5 h-3.5 mr-2" /> EDITOR
                         </div>
                          <Textarea
                            value={noteContent}
                            onChange={(e) => {
                              setNoteContent(e.target.value);
                              if (autoSyncEnabled && syncStatus !== 'syncing') {
                                setSyncStatus('pending');
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === '/' && !e.metaKey && !e.ctrlKey && currentView === 'notes') {
                                e.preventDefault();
                                setShowSlashMenu(true);
                              }
                            }}
                            className="flex-1 resize-none border-0 focus-visible:ring-0 font-mono text-sm p-4 sm:p-8 leading-relaxed bg-transparent overflow-auto"
                            placeholder="Write something... Type / for slash commands. Changes are encrypted with NIP-44 and synced to Nostr"
                          />
                       </Panel>

                       <PanelResizeHandle className="w-1 bg-border hover:bg-primary/30 transition-colors flex items-center justify-center">
                         <div className="w-1 h-8 bg-border/50 rounded-full" />
                       </PanelResizeHandle>

                       {/* Preview Panel */}
                       <Panel defaultSize={50} minSize={25} className="flex flex-col">
                         <div className="h-10 border-b bg-muted/50 flex items-center px-6 text-xs font-medium text-muted-foreground">
                           <Eye className="w-3.5 h-3.5 mr-2" /> PREVIEW
                         </div>
                           <div className="flex-1 p-4 sm:p-8 overflow-auto bg-white dark:bg-zinc-950">
                               {noteContent ? (
                                 <MarkdownRenderer
                                   content={noteContent}
                                   onToggleTask={handleToggleTask}
                                   resolveWikilink={resolveWikilink}
                                   onWikilinkClick={handleWikilinkClick}
                                 />
                              ) : (
                                <div className="flex h-full items-center justify-center text-center opacity-60">
                                  <div className="max-w-md">
                                    <BookOpen className="mx-auto h-16 w-16 text-muted-foreground mb-6" />
                                    <p className="text-muted-foreground text-lg">Clean reading view</p>
                                    <p className="text-sm text-muted-foreground mt-2">Your markdown will render beautifully here.</p>
                                  </div>
                                </div>
                              )}
                           </div>
                       </Panel>
                     </PanelGroup>
                     ) : notesMode === 'preview' ? (
                       <div className="flex-1 p-4 sm:p-8 overflow-auto bg-white dark:bg-zinc-950">
                          {noteContent ? (
                            <MarkdownRenderer
                              content={noteContent}
                              onToggleTask={handleToggleTask}
                              resolveWikilink={resolveWikilink}
                              onWikilinkClick={handleWikilinkClick}
                            />
                         ) : (
                           <div className="flex h-full items-center justify-center text-center opacity-60">
                             <div className="max-w-md">
                                <BookOpen className="mx-auto h-16 w-16 text-muted-foreground mb-6" />
                               <p className="text-muted-foreground text-lg">Clean reading view</p>
                               <p className="text-sm text-muted-foreground mt-2">Your decrypted document appears here. Toggle back to edit.</p>
                             </div>
                           </div>
                         )}
                      </div>
                   ) : (
                     <div className="flex-1 flex flex-col min-h-0">
                       <Textarea
                         value={noteContent}
                         onChange={(e) => {
                           setNoteContent(e.target.value);
                           if (autoSyncEnabled && syncStatus !== 'syncing') {
                             setSyncStatus('pending');
                           }
                         }}
                         onKeyDown={(e) => {
                           if (e.key === '/' && !e.metaKey && !e.ctrlKey && currentView === 'notes') {
                             e.preventDefault();
                             setShowSlashMenu(true);
                           }
                         }}
                          className="flex-1 resize-none border-0 focus-visible:ring-0 font-mono text-sm p-4 sm:p-8 leading-relaxed h-full bg-transparent overflow-auto"
                          placeholder="Write something... Type / for slash commands. Changes are encrypted with NIP-44 and synced to Nostr"
                        />
                      </div>
                    )}

                  </div>
                  </>
                )}
              </div>
              )}

            {user && currentView === 'database' && (
              <Card className="max-w-5xl mx-auto">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Database • Tasks</CardTitle>
                      <p className="text-sm text-muted-foreground">Fully editable table. Rows publish as encrypted kind 30078 events to Nostr.</p>
                    </div>
                    <Button 
                      onClick={() => {
                        const newRow = {
                          id: Date.now().toString(),
                          task: "New Task",
                          status: "Todo",
                          assignee: "You",
                          due: "Soon",
                          d: `db-task-${Date.now().toString(36)}`
                        };
                        setDatabaseRows([...databaseRows, newRow]);
                        publishDatabaseRow(newRow);
                      }}
                      size="sm"
                    >
                      + Add Row
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Task</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Assignee</TableHead>
                        <TableHead>Due</TableHead>
                        <TableHead className="w-12"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {databaseRows.map((row, i) => (
                        <TableRow key={row.id || i}>
                          <TableCell className="font-medium">
                            <input
                              type="text"
                              value={row.task}
                              onChange={(e) => {
                                const updated = [...databaseRows];
                                updated[i].task = e.target.value;
                                setDatabaseRows(updated);
                              }}
                              onBlur={() => publishDatabaseRow(row)}
                              className="bg-transparent border-0 focus:border focus:border-ring p-1 w-full rounded text-sm"
                            />
                          </TableCell>
                          <TableCell>
                            <select
                              value={row.status}
                              onChange={(e) => {
                                const updated = [...databaseRows];
                                updated[i].status = e.target.value;
                                setDatabaseRows(updated);
                                publishDatabaseRow(updated[i]);
                              }}
                              className={`inline-block px-3 py-1 text-xs rounded-full border bg-transparent text-sm cursor-pointer ${
                                row.status === 'Done' ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 border-green-200' : 
                                row.status === 'In Progress' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 border-blue-200' : 
                                'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300 border-yellow-200'
                              }`}
                            >
                              <option value="Todo">Todo</option>
                              <option value="In Progress">In Progress</option>
                              <option value="Done">Done</option>
                            </select>
                          </TableCell>
                          <TableCell>
                            <input
                              type="text"
                              value={row.assignee}
                              onChange={(e) => {
                                const updated = [...databaseRows];
                                updated[i].assignee = e.target.value;
                                setDatabaseRows(updated);
                              }}
                              onBlur={() => publishDatabaseRow(row)}
                              className="bg-transparent border-0 focus:border focus:border-ring p-1 w-full rounded text-sm"
                            />
                          </TableCell>
                          <TableCell>
                            <input
                              type="text"
                              value={row.due}
                              onChange={(e) => {
                                const updated = [...databaseRows];
                                updated[i].due = e.target.value;
                                setDatabaseRows(updated);
                              }}
                              onBlur={() => publishDatabaseRow(row)}
                              className="bg-transparent border-0 focus:border focus:border-ring p-1 w-full rounded text-sm"
                            />
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-destructive hover:text-destructive"
                              onClick={() => {
                                setDatabaseRows(databaseRows.filter((_, idx) => idx !== i));
                              }}
                            >
                               <X className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {databaseRows.length === 0 && (
                    <div className="text-center py-12 text-muted-foreground">
                      No rows yet. Add some tasks above — they will be published to Nostr.
                    </div>
                  )}
                  <p className="text-[10px] text-muted-foreground mt-4 text-center">
                    Changes auto-publish to your relays as encrypted events. Viewable only by you.
                  </p>
                </CardContent>
              </Card>
            )}

            {user && currentView === 'board' && (
              <div className="max-w-5xl mx-auto">
                <Card>
                  <CardHeader>
                    <CardTitle>Kanban Board (Demo)</CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-3 gap-4">
                    {['Backlog', 'In Progress', 'Done'].map((col) => (
                      <div key={col} className="bg-muted/50 rounded-2xl p-4">
                        <div className="font-medium mb-4 flex items-center justify-between">
                          {col}
                          <span className="text-xs bg-background px-2 py-1 rounded">3</span>
                        </div>
                        <div className="space-y-3">
                          {[1,2,3].map(i => (
                            <div key={i} className="bg-card p-4 rounded-xl shadow-sm border text-sm">
                              Task {i} in {col.toLowerCase()}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </div>
      </div>

       {/* Slash Command Menu */}
       <Dialog open={showSlashMenu} onOpenChange={setShowSlashMenu}>
         <DialogContent className="p-0 overflow-hidden">
           <DialogTitle className="sr-only">Slash Commands</DialogTitle>
           <Command>
            <CommandInput placeholder="Type a command... (e.g. heading, list, table)" />
            <CommandList>
              <CommandEmpty>No commands found.</CommandEmpty>
              <CommandGroup heading="Text Blocks">
                <CommandItem 
                  onSelect={() => {
                    setNoteContent(prev => prev + '\n\n# Heading 1\n\n');
                    setShowSlashMenu(false);
                  }}
                >
                  Heading 1
                  <CommandShortcut>⌘1</CommandShortcut>
                </CommandItem>
                <CommandItem 
                  onSelect={() => {
                    setNoteContent(prev => prev + '\n\n## Heading 2\n\n');
                    setShowSlashMenu(false);
                  }}
                >
                  Heading 2
                </CommandItem>
                <CommandItem 
                  onSelect={() => {
                    setNoteContent(prev => prev + '\n- List item 1\n- List item 2\n');
                    setShowSlashMenu(false);
                  }}
                >
                  Bullet List
                </CommandItem>
              </CommandGroup>
              <CommandGroup heading="Special Blocks">
                <CommandItem 
                  onSelect={() => {
                    setNoteContent(prev => prev + '\n\n> **Note:** This is an important note\n\n');
                    setShowSlashMenu(false);
                  }}
                >
                  Callout
                </CommandItem>
                <CommandItem 
                  onSelect={() => {
                    setNoteContent(prev => prev + '\n\n| Header 1 | Header 2 |\n| -------- | -------- |\n| Cell 1 | Cell 2 |\n| Cell 3 | Cell 4 |\n\n');
                    setShowSlashMenu(false);
                  }}
                >
                  Table
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
         </DialogContent>
       </Dialog>

       {/* New Document Modal */}
       <Dialog open={showNewDocModal} onOpenChange={setShowNewDocModal}>
         <DialogContent>
           <DialogTitle>New Document</DialogTitle>
           <DialogDescription>Give your new private document a name.</DialogDescription>
           <Input
             value={newDocTitleInput}
             onChange={(e) => setNewDocTitleInput(e.target.value)}
             placeholder="e.g. Meeting Notes, Project Plan..."
             className="mt-2"
             autoFocus
             onKeyDown={(e) => {
               if (e.key === 'Enter') {
                 handleNewDocumentConfirm();
               }
             }}
           />
           <div className="flex justify-end gap-3 mt-6">
             <Button
               type="button"
               variant="outline"
               onClick={() => {
                 setShowNewDocModal(false);
                 setNewDocTitleInput('');
               }}
             >
               Cancel
             </Button>
             <Button type="button" onClick={handleNewDocumentConfirm}>
               Create Document
             </Button>
           </div>
         </DialogContent>
        </Dialog>

        {/* Rename Document Modal */}
        <Dialog open={showRenameModal} onOpenChange={setShowRenameModal}>
          <DialogContent>
            <DialogTitle>Rename Document</DialogTitle>
            <DialogDescription>Enter a new name for this private document.</DialogDescription>
            <Input
              value={renameTitleInput}
              onChange={(e) => setRenameTitleInput(e.target.value)}
              placeholder="New document name..."
              className="mt-2"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleRenameConfirm();
                }
              }}
            />
            <div className="flex justify-end gap-3 mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowRenameModal(false);
                  setRenameDocD(null);
                  setRenameTitleInput('');
                }}
              >
                Cancel
              </Button>
              <Button type="button" onClick={handleRenameConfirm}>
                Rename Document
              </Button>
            </div>
          </DialogContent>
        </Dialog>

         {/* New Folder Modal */}
         <Dialog open={showNewFolderModal} onOpenChange={setShowNewFolderModal}>
           <DialogContent>
             <DialogTitle>New Folder</DialogTitle>
             <DialogDescription>
               Give your new folder a name.
               {newFolderParentPath !== '/' && (
                 <span className="block mt-1 text-xs text-muted-foreground">
                   Inside: <code className="bg-muted px-1 rounded">{newFolderParentPath}</code>
                 </span>
               )}
             </DialogDescription>
             <Input
               value={newFolderNameInput}
               onChange={(e) => setNewFolderNameInput(e.target.value)}
               placeholder="e.g. Projects, Archive, Work..."
               className="mt-2"
               autoFocus
               onKeyDown={(e) => {
                 if (e.key === 'Enter') handleNewFolderConfirm();
               }}
             />
             <div className="flex justify-end gap-3 mt-6">
               <Button type="button" variant="outline" onClick={() => { setShowNewFolderModal(false); setNewFolderNameInput(''); }}>
                 Cancel
               </Button>
               <Button type="button" onClick={handleNewFolderConfirm}>
                 Create Folder
               </Button>
             </div>
           </DialogContent>
         </Dialog>

         {/* Rename Folder Modal */}
         <Dialog open={showRenameFolderModal} onOpenChange={setShowRenameFolderModal}>
           <DialogContent>
             <DialogTitle>Rename Folder</DialogTitle>
             <DialogDescription>Enter a new name for this folder. All documents inside will be updated.</DialogDescription>
             <Input
               value={renameFolderInput}
               onChange={(e) => setRenameFolderInput(e.target.value)}
               placeholder="e.g. Projects, Archive, Work..."
               className="mt-2"
               autoFocus
               onKeyDown={(e) => {
                 if (e.key === 'Enter') handleRenameFolderConfirm();
               }}
             />
             <div className="flex justify-end gap-3 mt-6">
               <Button type="button" variant="outline" onClick={() => { setShowRenameFolderModal(false); setRenameFolderPath(null); setRenameFolderInput(''); }}>
                 Cancel
               </Button>
               <Button type="button" onClick={handleRenameFolderConfirm}>
                 Rename Folder
               </Button>
             </div>
           </DialogContent>
         </Dialog>

         {/* Delete Confirmation Dialog */}
         <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
           <AlertDialogContentPrimitive>
             <AlertDialogHeader>
               <AlertDialogTitle className="flex items-center gap-2">
                 <AlertTriangle className="w-5 h-5 text-destructive" />
                 {deleteTarget?.isFolder ? 'Delete Folder?' : 'Delete Document?'}
               </AlertDialogTitle>
                <AlertDialogDescriptionPrimitive>
                  {deleteTarget?.isFolder ? (
                    <>
                      All documents inside <strong>"{deleteTarget.name}"</strong> will be moved to Trash. The encrypted Nostr events remain on your relays. You can restore them from <strong>Settings → Sync → Trash</strong>.
                    </>
                  ) : (
                    <>
                      <strong>"{deleteTarget?.name}"</strong> will be moved to Trash. The encrypted Nostr event remains on your relays. You can restore it from <strong>Settings → Sync → Trash</strong>.
                    </>
                  )}
                </AlertDialogDescriptionPrimitive>
             </AlertDialogHeader>
             <AlertDialogFooter>
               <AlertDialogCancel onClick={() => { setShowDeleteConfirm(false); setDeleteTarget(null); }}>
                 Cancel
               </AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive hover:bg-destructive/90"
                  onClick={async () => {
                    if (!deleteTarget) return;
                    if (deleteTarget.isFolder && deleteTarget.folderPath) {
                      const folderPrefix = deleteTarget.folderPath.replace(/\/$/, '');
                      // Delete all files in folder
                      const folderFiles = documentList.filter((n: any) => {
                        const p = n.payload?.path || '';
                        return p.startsWith(folderPrefix + '/') || p === folderPrefix;
                      });
                      for (const f of folderFiles) {
                        const d = f.tags?.[0]?.[1];
                        if (d) await deleteFile(d);
                      }
                      // Remove the folder (and any sub-folders) from the vault index folders[] array
                      if (user?.signer?.nip44 && user.pubkey) {
                        const now = Math.floor(Date.now() / 1000);
                        const currentIndex = decryptedNotes.find((n: any) => n.isIndex);
                        const existingFiles: any[] = currentIndex?.payload?.files || [];
                        const existingFolders: string[] = currentIndex?.payload?.folders || [];
                        const updatedFolders = existingFolders.filter(
                          (fp: string) => fp !== folderPrefix && !fp.startsWith(folderPrefix + '/')
                        );
                        // Only publish if the folders list actually changed
                        if (updatedFolders.length !== existingFolders.length) {
                          const indexPayload = {
                            name: "Nostrion Workspace",
                            description: "Private encrypted documents synced via Nostr",
                            files: existingFiles,
                            folders: updatedFolders,
                            deleted: currentIndex?.payload?.deleted || [],
                            updated: now,
                          };
                          try {
                            const indexEncrypted = await user.signer.nip44.encrypt(user.pubkey, JSON.stringify(indexPayload));
                            await publishEvent({
                              kind: KIND_VAULT_INDEX, created_at: now,
                              tags: [['d', activeVaultDRef.current], ['encrypted', 'nip44']],
                              content: indexEncrypted,
                            } as any);
                            setDecryptedNotes(prev => prev.map((n: any) => {
                              if (!n.isIndex) return n;
                              return { ...n, payload: { ...n.payload, folders: updatedFolders, updated: now } };
                            }));
                          } catch (err) {
                            console.error('Failed to remove folder from index:', err);
                          }
                        }
                      }
                      toast({ title: `Folder "${deleteTarget.name}" deleted`, description: folderFiles.length > 0 ? `${folderFiles.length} document(s) moved to Trash. Restore from Settings → Sync.` : 'Empty folder removed.' });
                    } else if (deleteTarget.dTag) {
                      await deleteFile(deleteTarget.dTag);
                    }
                    setShowDeleteConfirm(false);
                    setDeleteTarget(null);
                  }}
                >
                 <Trash2 className="w-4 h-4 mr-2" /> Delete
               </AlertDialogAction>
             </AlertDialogFooter>
           </AlertDialogContentPrimitive>
         </AlertDialog>

          {/* Obsidian-style Full Settings Modal */}
        <Dialog open={showSettings} onOpenChange={(open) => {
          setShowSettings(open);
          if (!open) setMobileSettingsShowContent(false);
        }}>
          <DialogContent className="max-w-5xl h-[92vh] sm:h-[88vh] p-0 overflow-hidden" style={{ width: 'min(96vw, 900px)' }}>
            <DialogTitle className="sr-only">Settings</DialogTitle>
            <DialogDescription className="sr-only">Configure your Nostrion workspace, relays, privacy, and appearance.</DialogDescription>

            {/* Shared settings content block — rendered inside whichever panel is active */}
            {(() => {
              const settingsTabs = [
                { id: 'general' as const, label: 'General', icon: Settings },
                { id: 'appearance' as const, label: 'Appearance', icon: Sun },
                { id: 'editor' as const, label: 'Editor', icon: Edit3 },
                { id: 'sync' as const, label: 'Sync', icon: RefreshCw },
                { id: 'relays' as const, label: 'Relays', icon: Wifi },
                { id: 'privacy' as const, label: 'Privacy', icon: Lock },
              ] as const;

             const settingsContent = (
               <div>
                 {activeSettingsTab === 'relays' && (
                   <div>
                     <h2 className="text-xl sm:text-2xl font-semibold mb-2">Network &amp; Relays</h2>
                     <p className="text-muted-foreground mb-6">Your data is synced across these Nostr relays.</p>
                     <RelayListManager />
                   </div>
                 )}
                 {activeSettingsTab === 'general' && (
                   <div>
                     <h2 className="text-xl sm:text-2xl font-semibold mb-2">General</h2>
                     <p className="text-muted-foreground">Workspace preferences, encryption settings, and data management.</p>
                     <div className="space-y-6 mt-6">
                       <div className="space-y-4">
                         <div className="flex items-start justify-between gap-4">
                           <div>
                             <Label className="text-base font-medium">Auto-sync to Nostr</Label>
                             <p className="text-sm text-muted-foreground">Automatically publish changes after edits (recommended)</p>
                           </div>
                           <Switch checked={autoSyncEnabled} onCheckedChange={setAutoSyncEnabled} className="flex-shrink-0" />
                         </div>
                         {!autoSyncEnabled && (
                           <Button onClick={() => { saveNoteToNostr(noteContent, selectedNoteD || undefined); setSyncStatus('syncing'); setTimeout(() => setSyncStatus('synced'), 800); }} variant="outline" className="w-full flex items-center gap-2">
                             <RefreshCw className="w-4 h-4" /> Sync Now
                           </Button>
                         )}
                       </div>
                     </div>
                   </div>
                 )}
                 {activeSettingsTab === 'appearance' && (
                   <div>
                     <h2 className="text-xl sm:text-2xl font-semibold mb-2">Appearance</h2>
                     <p className="text-muted-foreground mb-6">Choose your preferred color mode.</p>
                     <div>
                       <Label className="text-base font-medium mb-4 block">Theme Mode</Label>
                       <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                         {([
                           { value: 'light' as const, label: 'Light', desc: 'White background with black text', icon: Sun, preview: 'bg-white border-2 border-black' },
                           { value: 'dark' as const, label: 'Dark', desc: 'Black background with white text', icon: Moon, preview: 'bg-black border-2 border-white' },
                           { value: 'system' as const, label: 'System', desc: 'Follow your OS', icon: Settings, preview: 'bg-gradient-to-br from-white via-gray-100 to-black border border-black' },
                         ]).map((option) => {
                           const Icon = option.icon;
                           const isActive = theme === option.value;
                           return (
                             <div key={option.value} onClick={() => setTheme(option.value)} className={`group border rounded-2xl p-4 sm:p-6 cursor-pointer transition-all hover:shadow-md flex flex-row sm:flex-col items-center gap-4 sm:gap-0 sm:text-center ${isActive ? 'border-primary bg-accent shadow-sm' : 'hover:border-accent hover:bg-accent/30'}`}>
                               <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl sm:mb-4 flex items-center justify-center flex-shrink-0 ${option.preview} shadow-inner`}>
                                 <Icon className={`w-6 h-6 sm:w-8 sm:h-8 ${isActive ? 'text-primary' : 'text-muted-foreground'}`} />
                               </div>
                               <div>
                                 <div className="font-semibold mb-0.5">{option.label}</div>
                                 <p className="text-xs sm:text-sm text-muted-foreground">{option.desc}</p>
                               </div>
                             </div>
                           );
                         })}
                       </div>
                     </div>
                   </div>
                 )}
                 {activeSettingsTab === 'editor' && (
                   <div>
                     <h2 className="text-xl sm:text-2xl font-semibold mb-2">Editor</h2>
                     <p className="text-muted-foreground">Slash commands, markdown behavior, and editor appearance.</p>
                     <div className="mt-8 p-8 border border-dashed rounded-3xl text-center text-sm text-muted-foreground">
                       Editor preferences coming soon
                     </div>
                   </div>
                 )}
                 {activeSettingsTab === 'sync' && (
                   <div>
                     <h2 className="text-xl sm:text-2xl font-semibold mb-2 flex items-center gap-2">
                       <RefreshCw className="w-5 h-5" /> Sync Manager
                     </h2>
                     <p className="text-muted-foreground mb-6">Manage your vault index and recover deleted documents.</p>
                     <div className="space-y-4">
                       <div className="p-4 sm:p-6 border rounded-2xl bg-card">
                         <div className="flex items-center gap-2 mb-1">
                           <RefreshCw className="w-5 h-5 text-muted-foreground" />
                           <Label className="text-base font-medium">Rebuild Vault Index</Label>
                         </div>
                         <p className="text-sm text-muted-foreground mb-4">Scan all your Nostr events and rebuild the vault index. Recovers documents from Onyx or previous versions that may be missing from the sidebar.</p>
                         <Button onClick={rebuildVaultIndex} className="w-full flex items-center gap-2" variant="outline">
                           <RefreshCw className="w-4 h-4" /> Rebuild Index from All Events
                         </Button>
                       </div>
                       <div className="p-4 sm:p-6 border rounded-2xl bg-card">
                         <div className="flex items-center gap-2 mb-1">
                           <Trash2 className="w-5 h-5 text-muted-foreground" />
                           <Label className="text-base font-medium">Trash</Label>
                           {deletedFiles.length > 0 && <span className="text-xs bg-muted px-2 py-0.5 rounded-full text-muted-foreground">{deletedFiles.length}</span>}
                         </div>
                         <p className="text-sm text-muted-foreground mb-4">Deleted documents. The encrypted Nostr events remain on your relays — only the vault index entry is removed. Restore to bring them back.</p>
                         {deletedFiles.length === 0 ? (
                           <div className="text-center py-6 border border-dashed rounded-2xl text-sm text-muted-foreground">Trash is empty.</div>
                         ) : (
                           <div className="space-y-2">
                             {deletedFiles.map((f) => (
                               <div key={f.d} className="flex items-center justify-between p-3 bg-muted/50 rounded-xl gap-3">
                                 <div className="flex items-center gap-3 min-w-0">
                                   <File className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                   <div className="min-w-0">
                                     <div className="text-sm font-medium truncate">{f.title}</div>
                                     <div className="text-xs text-muted-foreground truncate">{f.path}</div>
                                     {f.deletedAt > 0 && <div className="text-xs text-muted-foreground/70">Deleted: {new Date(f.deletedAt * 1000).toLocaleString()}</div>}
                                   </div>
                                 </div>
                                 <Button variant="outline" size="sm" className="flex-shrink-0 flex items-center gap-1.5 text-xs" onClick={() => restoreFile({ dTag: f.d, title: f.title, path: f.path, modifiedAt: f.deletedAt || 0 })} disabled={recoveringScannedD === f.d}>
                                   {recoveringScannedD === f.d ? <><RefreshCw className="w-3 h-3 animate-spin" /> Restoring…</> : <><RotateCcw className="w-3 h-3" /> Restore</>}
                                 </Button>
                               </div>
                             ))}
                           </div>
                         )}
                       </div>
                       <div className="p-4 sm:p-6 border rounded-2xl bg-card">
                         <div className="flex items-center justify-between mb-1">
                           <div className="flex items-center gap-2">
                             <RotateCcw className="w-5 h-5 text-muted-foreground" />
                             <Label className="text-base font-medium">File Recovery</Label>
                             {scannedRecoverableFiles.length > 0 && <span className="text-xs bg-muted px-2 py-0.5 rounded-full text-muted-foreground">{scannedRecoverableFiles.length}</span>}
                           </div>
                           <Button variant="outline" size="sm" className="flex items-center gap-1.5 text-xs" onClick={scanForRecoverableFiles} disabled={scanLoading}>
                             {scanLoading ? <><RefreshCw className="w-3 h-3 animate-spin" /> Scanning…</> : <><RefreshCw className="w-3 h-3" /> Scan</>}
                           </Button>
                         </div>
                         <p className="text-sm text-muted-foreground mb-4">Scan your relays for encrypted documents that exist on Nostr but aren't in your current workspace.</p>
                         {scanMessage && <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3 p-3 bg-muted/40 rounded-xl"><AlertTriangle className="w-4 h-4 flex-shrink-0 text-amber-500" />{scanMessage}</div>}
                         {scannedRecoverableFiles.length === 0 && !scanMessage && <div className="text-center py-6 border border-dashed rounded-2xl text-sm text-muted-foreground">Click <strong>Scan</strong> to search your relays for recoverable documents.</div>}
                         {scannedRecoverableFiles.length > 0 && (
                           <div className="space-y-2">
                             {scannedRecoverableFiles.map((f) => (
                               <div key={f.dTag} className="flex items-center justify-between p-3 bg-muted/50 rounded-xl gap-3">
                                 <div className="flex items-center gap-3 min-w-0">
                                   <File className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                   <div className="min-w-0">
                                     <div className="text-sm font-medium truncate">{f.title}</div>
                                     <div className="text-xs text-muted-foreground truncate">{f.path}</div>
                                     {f.modifiedAt > 0 && <div className="text-xs text-muted-foreground/70">Last modified: {new Date(f.modifiedAt * 1000).toLocaleString()}</div>}
                                   </div>
                                 </div>
                                 <Button variant="outline" size="sm" className="flex-shrink-0 flex items-center gap-1.5 text-xs" onClick={() => restoreFile(f)} disabled={recoveringScannedD === f.dTag}>
                                   {recoveringScannedD === f.dTag ? <><RefreshCw className="w-3 h-3 animate-spin" /> Recovering…</> : <><RotateCcw className="w-3 h-3" /> Recover</>}
                                 </Button>
                               </div>
                             ))}
                           </div>
                         )}
                       </div>
                     </div>
                   </div>
                 )}
                 {activeSettingsTab === 'privacy' && (
                   <div>
                     <h2 className="text-xl sm:text-2xl font-semibold mb-2 flex items-center gap-2">
                       <Lock className="w-5 h-5" /> Privacy &amp; Data
                     </h2>
                     <p className="text-muted-foreground mb-6">Nostrion is designed for maximum privacy. All notes and databases are end-to-end encrypted with NIP-44.</p>
                     <div className="space-y-4">
                       <div className="flex items-start justify-between gap-4 p-4 sm:p-6 border rounded-2xl bg-card">
                         <div className="space-y-1 flex-1">
                           <Label className="text-base font-medium">Clear cache on logout</Label>
                           <p className="text-sm text-muted-foreground">Automatically clear DM cache, local preferences, and NWC connections when logging out. Recommended for shared devices.</p>
                         </div>
                         <Switch checked={clearCacheOnLogout} onCheckedChange={setClearCacheOnLogout} className="flex-shrink-0 mt-0.5" />
                       </div>
                       <div className="flex items-start justify-between gap-4 p-4 sm:p-6 border rounded-2xl bg-card">
                         <div className="space-y-1 flex-1">
                           <Label className="text-base font-medium">Auto-clear after inactivity</Label>
                           <p className="text-sm text-muted-foreground">Automatically log out and clear sensitive data after period of inactivity. (Coming soon)</p>
                         </div>
                         <Switch checked={autoClearInactivity} onCheckedChange={setAutoClearInactivity} disabled className="flex-shrink-0 mt-0.5" />
                       </div>
                       <div className="p-4 sm:p-6 border rounded-2xl bg-card">
                         <Label className="text-base font-medium">Rebuild Documents Index</Label>
                         <p className="text-sm text-muted-foreground mb-4">Scan all your Nostr events and rebuild the vault index. Recovers documents that may be missing from the sidebar.</p>
                         <Button onClick={rebuildVaultIndex} className="w-full flex items-center gap-2" variant="outline">
                           <RefreshCw className="w-4 h-4" /> Rebuild Vault Index from All Events
                         </Button>
                       </div>
                       <Button variant="destructive" className="w-full" onClick={() => setShowWipeConfirm(true)}>
                         Wipe All Local Data &amp; Log Out
                       </Button>
                       <p className="text-[10px] text-center text-muted-foreground">Clears IndexedDB DM cache, localStorage, and logs out. Resets to fresh state.</p>
                     </div>
                   </div>
                 )}
               </div>
             );

              return isMobile ? (
                /* ── Mobile: single-column drill-down ── */
                <div className="flex flex-col w-full h-full overflow-hidden">
                  {!mobileSettingsShowContent ? (
                    /* Nav list */
                    <div className="flex flex-col h-full">
                      <div className="flex items-center gap-3 px-5 py-4 border-b flex-shrink-0">
                        <div className="w-8 h-8 bg-primary rounded-xl flex items-center justify-center text-primary-foreground font-bold text-sm">N</div>
                        <div className="font-semibold text-lg">Settings</div>
                      </div>
                      <nav className="flex-1 overflow-auto p-3 space-y-1">
                        {settingsTabs.map((tab) => (
                          <div key={tab.id} onClick={() => { setActiveSettingsTab(tab.id); setMobileSettingsShowContent(true); }} className="flex items-center gap-3 px-4 py-3.5 rounded-xl cursor-pointer transition-all text-sm hover:bg-accent/50 active:bg-accent">
                            <tab.icon className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
                            <span className="flex-1">{tab.label}</span>
                            <ChevronRight className="w-4 h-4 text-muted-foreground/50" />
                          </div>
                        ))}
                      </nav>
                      <div className="px-5 py-4 border-t text-[10px] text-muted-foreground flex-shrink-0">
                        Nostrion v0.1 • Private by default
                      </div>
                    </div>
                  ) : (
                    /* Drilled-in content */
                    <div className="flex flex-col h-full overflow-hidden">
                      <div className="flex items-center gap-2 px-3 py-3 border-b flex-shrink-0">
                        <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0" onClick={() => setMobileSettingsShowContent(false)}>
                          <ChevronLeft className="w-5 h-5" />
                        </Button>
                        <span className="font-semibold">{settingsTabs.find(t => t.id === activeSettingsTab)?.label}</span>
                      </div>
                      <div className="flex-1 overflow-auto p-5 bg-background">
                        {settingsContent}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* ── Desktop: side-by-side ── */
                <div className="flex h-full overflow-hidden">
                  <div className="w-48 border-r bg-muted/40 p-4 flex flex-col flex-shrink-0">
                    <div className="flex items-center gap-2.5 mb-6">
                      <div className="w-7 h-7 bg-primary rounded-lg flex items-center justify-center text-primary-foreground font-bold text-sm">N</div>
                      <div className="font-semibold text-base">Settings</div>
                    </div>
                    <nav className="space-y-0.5 flex-1">
                      {settingsTabs.map((tab) => (
                        <div key={tab.id} onClick={() => setActiveSettingsTab(tab.id)} className={`flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer transition-all text-sm ${activeSettingsTab === tab.id ? 'bg-accent font-medium shadow-sm' : 'hover:bg-accent/50 text-muted-foreground'}`}>
                          <tab.icon className="w-4 h-4 flex-shrink-0" />
                          <span>{tab.label}</span>
                        </div>
                      ))}
                    </nav>
                    <div className="pt-4 border-t text-[10px] text-muted-foreground">Nostrion v0.1 • Private by default</div>
                  </div>
                  <div className="flex-1 overflow-auto bg-background p-6 sm:p-8">
                    {settingsContent}
                  </div>
                </div>
              );
           })()}
          </DialogContent>
        </Dialog>

       {/* Wipe Confirmation Dialog */}
       <AlertDialog open={showWipeConfirm} onOpenChange={setShowWipeConfirm}>
         <AlertDialogContentPrimitive>
            <AlertDialogHeader>
              <AlertDialogTitle>Wipe All Local Data?</AlertDialogTitle>
              <AlertDialogDescriptionPrimitive>
                This will log you out, clear all cached notes/DMs, preferences, and NWC connections.
                This action cannot be undone.
              </AlertDialogDescriptionPrimitive>
            </AlertDialogHeader>
           <AlertDialogFooter>
             <AlertDialogCancel>Cancel</AlertDialogCancel>
             <AlertDialogAction
               onClick={handleWipeAllData}
               className="bg-red-600 hover:bg-red-700"
             >
               Wipe and Log Out
             </AlertDialogAction>
           </AlertDialogFooter>
         </AlertDialogContentPrimitive>
       </AlertDialog>
     </div>
   );
 };


export default Index;
